import DeviceTreeViewModel from "../modelTreeViews/DeviceTreeviewModel";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import { EventKey } from "../eventLogger/EventKeys";

import BaseModelMap from "./BaseModelMap";
import Channel, { filterChannelsByUserRole } from "./Channel";
import ConnectableObject from "./ConnectableObject";
import Connection, { ConnectionType } from "./Connection";
import type DeviceTemplate from "./DeviceTemplate";
import KnxInstanceData, { ValueLevels } from "./KnxInstanceData";
import {
  copyAllProperties,
  later,
  nameToShortName,
  productionAssert,
  sortArrayByIntProperty,
  sortArrayByProperty,
} from "./utils";
import KnxParameterViewer from "../components/KnxParameterViewer";
import {
  createKnxParameterViewerCache,
  type KnxParameterViewerCache,
} from "../components/KnxParameterViewer/KnxParameterViewerCache";
import { determineChannelTypes } from "../KnxProductDataTools/determineChannelTpyes";
import {
  createChannelsByComObjectNames,
  createChannelsByRule,
} from "../KnxProductDataTools/groupGeneralSignalsIntoChannelsForActor";
import {
  cleanupSignals,
  mapKnxChannelsToRequieremntChannelForButton,
  mapKnxChannelsToRequieremntChannels as mapKnxChannelsToRequieremntChannelsForActor,
} from "../KnxProductDataTools/UpdateChannels";
import { KnxBuildSceneInfos } from "../KnxProductDataTools/KnxBuildSceneInfos";
import { ChannelType, channelTypeInfo, DeviceType, areChannelTypesCompatible } from "./ChannelType";

import TemplateGroup, { getTemplateGroup } from "./TemplateGroup";
import {
  cleanUpDuplicateKnxChannelTypeProperty,
  mergeEnvironmentSignals,
  mergeTemperaturChannels,
} from "../KnxProductDataTools/groupGeneralSignalsIntoChannelsForButton";

import { ButtonType, createBaseDeviceProps, type BaseDeviceProps } from "./BaseDeviceProps";

//////////////////////////////////////////////////
import Logger from "../Logger";
const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import determineButtonComObjectFunction from "../KnxProductDataTools/determineButtonComObjectFunction";
import SignalTypes from "./SignalTypes";
import ButtonChannel from "./ButtonChannel";
import BaseObject from "./BaseObject";
import Room from "./Room";
import Cabinet from "./Cabinet";
import Signal from "./Signal";
import determineActorComObjectFunction from "../KnxProductDataTools/determineActorComObjectFunction";
import Browser from "../browser";
import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import type Button from "./Button";
import type { DeviceCabinetPlacement } from "./DeviceCabinetPlacement";
import { showGlobalWaitIndicator } from "../components/GlobalWaitIndicator";
import { InstallChecks } from "./InstallationChecks";
import { EP } from "../performance/PerfomanceItem";
import { showErrToast, showToast } from "../components/Toasts";
import { isDevelopmentEnvironment } from "../estada-config";
import { itx, noItx } from "../translationItx/itx";
import { KnxSceneInfos } from "./KnxSceneInfo";
import AllowedConections from "./AllowedConnections";
import WatchableValue from "./WatchableValue";
import DeviceProgramming from "./programming/DeviceProgramming";
import KnxRules, { Definitions } from "./KnxRules";
import { getChannelTypeForEnum } from "../KnxMappings/ButtonChannelType";
import KnxIds from "./KnxIds";
import { TAG_ENVIRONMENT, TAG_IGNORE, TAG_MANUAL_SIGNALS, TAG_SPECIAL, TAG_TX } from "./Constants.ts";
import Area from "./Area.ts";
import Line from "./Line.ts";
import { showConsitencyErrorsDialog } from "../components/ConsitencyErrorsDialog.tsx";
import { mergeAllSpecialSiganls } from "../KnxProductDataTools/mergeChannels.ts";
import type { MapChannelTypeConfig } from "../components/PanelLayout/MapChannelTypeConfig.ts";
import { PanelConfigs } from "../components/PanelLayout/PanelConfig.ts";
import { channelsSortedForDisplayFromDevice } from "./ChannelsSorting.tsx";
import { DeviceClass, DeviceClasses } from "./DeviceClasses.ts";
import { type KnxChapterItem } from "../components/KnxParameterViewer/KnxChapterItem.ts";
import { determineComObjectFunction } from "../components/KnxParameterViewer/ComObjectRefRef.tsx";
import DeviceLayoutDefinition from "./DeviceLayoutDefinition";
import { RockerConfig } from "./ButtonRockerConfig";
import { template } from "handlebars";
import { get } from "http";
import { any } from "three/src/nodes/TSL.js";
import { string, boolean } from "yup";
import data from "../../ConceptOne/src/data/C1_roomTemplates.ts";

const _FILENAME_ = "Device.ts"; // no itx

const CURRENT_EVAL_VERSION = 2; //

ObjectFactory.registerClass("Device", (data?: any, parent?: BaseModel) => {
  return new Device(data, parent);
});
//////////////////////////////////////////////////
export default class Device extends ConnectableObject {
  public get className(): string {
    return "Device"; // no itx
  }

  public getIsDevice(): boolean {
    return true;
  }

  get deviceType(): DeviceType {
    return this.getChannelType() as any as DeviceType;
  }

  // set deviceType(newValue) {
  //   this._channelType = newValue as any as ChannelType;
  // }

  public get isDevice(): boolean {
    return true;
  }

  get isDummyDeviceForTemplate(): boolean {
    return this.tag == "@T" || this.tag == "@A";
  }

  getChannelType(): ChannelType {
    if (this.template?.knxProductData.productInfo.ChannelType) {
      return getChannelTypeForEnum(this.template?.knxProductData.productInfo.ChannelType).channelType;
    }

    return this._channelType;
  }
  channels = new BaseModelMap<Channel>(Channel, this, "ch");
  knxChannels = new BaseModelMap<Channel>(Channel, this, "kc");

  // -----------------------------------------------------------------------------
  // cabinetPlacement
  // -----------------------------------------------------------------------------
  _cabinetPlacement?: DeviceCabinetPlacement;
  get cabinetPlacement(): DeviceCabinetPlacement | undefined {
    return this._cabinetPlacement;
  }

  set cabinetPlacement(newValue: DeviceCabinetPlacement | undefined) {
    if (this._cabinetPlacement === newValue) return;
    this.beforeChange("cabinetPlacement");
    this._cabinetPlacement = newValue;
    this.changed("cabinetPlacement");
  }

  // -----------------------------------------------------------------------------
  // deviceLayoutDefinition
  // -----------------------------------------------------------------------------
  private _deviceLayoutDefinition?: DeviceLayoutDefinition;

  get deviceLayoutDefinition(): DeviceLayoutDefinition {
    if (!this._deviceLayoutDefinition) {
      this._deviceLayoutDefinition = new DeviceLayoutDefinition(this);
    }
    return this._deviceLayoutDefinition;
  }

  // -----------------------------------------------------------------------------
  // templateId
  // -----------------------------------------------------------------------------
  _templateId: string = "";
  get templateId(): string {
    return this._templateId;
  }

  set templateId(newValue: string) {
    if (this._templateId === newValue) return;
    this.beforeChange("templateId");
    this._templateId = newValue;
    this.changed("templateId");
  }

  // -----------------------------------------------------------------------------
  // eV
  // -----------------------------------------------------------------------------
  _eV: number = 0;
  get eV(): number {
    return this._eV;
  }

  set eV(newValue: number) {
    if (this._eV === newValue) return;
    this.beforeChange("eV");
    this._eV = newValue;
    this.changed("eV");
  } // lastes "EvalVersion" of the device

  // -----------------------------------------------------------------------------
  // classification
  // -----------------------------------------------------------------------------
  _classification: DeviceClass | undefined;

  get classification(): DeviceClass {
    if (this._classification) {
      return this._classification;
    }
    // if (this.template?.classification) {
    //   return this.template.classification;//-> why?
    // }

    return DeviceClasses.Unknown;
  }
  set classification(newValue: DeviceClass) {
    this.beforeChange("classification");
    this._classification = newValue == DeviceClasses.Unknown ? undefined : newValue;

    this.changed("classification");
  }

  // -----------------------------------------------------------------------------

  private _physicalAddress: string = ""; /// max 1-255 // first initiated as empty string to trigger findempty from fullphysicaladress
  private _lineAddress: string = "1"; //max 1-15
  private _areaAddress: string = "1"; //max 1-15

  //
  // not activate yet MWE 3.2.2026
  //

  // -----------------------------------------------------------------------------
  // applicationProgramId
  // -----------------------------------------------------------------------------
  _applicationProgramId: string = "";
  get applicationProgramId(): string {
    return this._applicationProgramId;
  }

  set applicationProgramId(newValue: string) {
    if (this._applicationProgramId === newValue) return;
    this.beforeChange("applicationProgramId");
    this._applicationProgramId = newValue;
    this.changed("applicationProgramId");
  }

  // -----------------------------------------------------------------------------

  private reEvalCount = 0;
  private _reEvalKnxMetadataNeeded = true; // initaly the knxdatgetAppa is not evaluated

  private _deviceObjectTypes: Record<string, number> = {};

  public get reEvalKnxMetadataNeeded() {
    return this._reEvalKnxMetadataNeeded;
  }
  public set reEvalKnxMetadataNeeded(value) {
    this._reEvalKnxMetadataNeeded = value;
  }

  public setReEvalNeeded(reason: string) {
    if (isDevelopmentEnvironment()) {
      if (!this._reEvalKnxMetadataNeeded) {
        logger.log(`setReEvalNeeded: ${reason} reEvalCount=${this.reEvalCount}`);
      }
    }

    this._reEvalKnxMetadataNeeded = true;
  }

  maxChannelSize = 24;

  get isPanel(): boolean {
    try {
      return this.panelConfig ? true : false;
    } catch (error) {}
    return false;
  }

  get isDecentralActor(): boolean {
    return this.classification == DeviceClasses.DecentralActor; // no itx
  }

  get lineAddress(): string {
    return this._lineAddress || "";
  }

  set lineAddress(newValue) {
    this.beforeChange("lineAddress");
    if (parseInt(newValue) <= 15 && parseInt(newValue) >= 0) {
      this._lineAddress = newValue;
    }

    this.changed("lineAddress");
  }

  get areaAddress(): string {
    return this._areaAddress || "";
  }

  set areaAddress(newValue) {
    this.beforeChange("areaAddress");
    if (parseInt(newValue) <= 15 && parseInt(newValue) >= 0) {
      this._areaAddress = newValue;
    }

    this.changed("areaAddress");
  }

  get physicalAddress(): string {
    return this._physicalAddress ?? "";
  }

  set physicalAddress(newValue) {
    this.beforeChange("physicalAddress");
    this._physicalAddress = newValue;

    this.changed("physicalAddress");
  }

  //////////////////////////////////////////////////////////////////////////////

  get area(): Area | undefined {
    return state.topologies.findFirst((a) => a.areaNumber.toString() == this.areaAddress);
  }

  get line(): Line | undefined {
    return this.area?.lines.findFirst((l) => l.lineNumber.toString() == this.lineAddress);
  }

  //////////////////////////////////////////////////////////////////////////////

  setFullPhysicalAddress(newValue: string) {
    const ss = newValue.split(".");
    if (ss.length != 3) {
      logger.error("invalid physical address: ", newValue);
      return;
    }

    this._areaAddress = ss[0];
    this._lineAddress = ss[1];
    this._physicalAddress = ss[2];

    this.raiseOnChange("physicalAddress");
  }

  setPhysicalAddressWithAutoRenumbering(newValue) {
    function findIfPlaceIsEmpty(device: Device, value: string): boolean {
      let res = true;
      state.allDevices().forEach((dev) => {
        if (dev.id != device.id && !channelTypeInfo(dev.channelType).isSupply) {
          if (
            dev.lineAddress == device.lineAddress &&
            dev.areaAddress == device.areaAddress &&
            dev.physicalAddress == value
          ) {
            res = false;
          }
        }
      });
      return res;
    }

    if (parseInt(newValue) <= 255 && parseInt(newValue) >= 0 && newValue !== "-") {
      // ist noch nicht belegt
      if (findIfPlaceIsEmpty(this, newValue)) {
        this._physicalAddress = newValue;
        this.flagErrors({ doublePhysicalAdresses: "" }, this.getFullPhysicalAdressIsDouble());
      } else {
        // find empty place
        let i = 2;

        while (!findIfPlaceIsEmpty(this, `${i}`) || i >= 255) {
          i++;
        }
        this._physicalAddress = `${i}`;
        this.flagErrors({ doublePhysicalAdresses: "" }, this.getFullPhysicalAdressIsDouble());
      }
    } else {
      this._physicalAddress = newValue;
    }

    this.raiseOnChange("physicalAddress");
  }

  get fullPhysicalAddress(): string {
    return `${this.areaAddress}.${this.lineAddress}.${this.physicalAddress}`;
  }

  get deviceObjectTypes(): Record<string, number> {
    return this._deviceObjectTypes;
  }

  set deviceObjectTypes(objTypes: Record<string, number>) {
    if (Object.keys(objTypes).length > 0) {
      this._deviceObjectTypes = objTypes;
    }
  }

  mediumTypeAddress(mediumType: string) {
    const mediumTypeCollection = { TP: "1", RF: "2", PL: "3", IP: "4" };
    return mediumTypeCollection[mediumType as keyof typeof mediumTypeCollection] || "1";
  }

  // Technical ETS synchronization metadata. It intentionally bypasses undo tracking.
  etsId: string = "";

  // -----------------------------------------------------------------------------
  // installChecks
  // -----------------------------------------------------------------------------
  _installChecks?: InstallChecks;

  get installChecks(): InstallChecks {
    if (!this._installChecks) {
      this._installChecks = new InstallChecks(this.id);
    }
    return this._installChecks;
  }

  // -----------------------------------------------------------------------------
  // knxInstanceData
  // -----------------------------------------------------------------------------
  _knxInstanceData: KnxInstanceData = new KnxInstanceData(null, this);
  get knxInstanceData(): KnxInstanceData {
    return this._knxInstanceData;
  }

  set knxInstanceData(newValue: KnxInstanceData) {
    if (this._knxInstanceData === newValue) return;
    this.beforeChange("knxInstanceData");
    this._knxInstanceData = newValue;
    this.changed("knxInstanceData");
  }

  // -----------------------------------------------------------------------------
  /////////////////////////////////////////////////////////////////////

  // Cache the evaluated viewer together with the window that created it.

  knxParameterViewer = new WatchableValue<KnxParameterViewerCache | undefined>(undefined);
  currentKnxChapter = new WatchableValue<KnxChapterItem | undefined>(undefined);
  currentKnxChapterId = "";
  knxChapters: Array<KnxChapterItem> = [];

  ////////////////////////////////////////////////////////////////////

  knxParameterViewerHasRundOnce = false;

  definitions: Definitions | undefined; // used to store the result of definitions

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    this.treeViewModel = new DeviceTreeViewModel(this);

    //
    // removed: mwe 16.12.2024 we should not do this during "loadFromEstadaStorage"
    // is now called teh first time "physicalAddress" is "get"
    //
    // this.findFreePhysicalAddress();

    copyAllProperties(data, this);
  }

  /////////////////////////////////////////////////////////////////////

  get isDali(): boolean {
    if (this.template) {
      this.template?.knxProductData.productInfo.IsDali == "true";
    }
    return this._isDali;
  }

  set isDali(newValue: boolean) {
    this.beforeChange("isDali");
    if (this.template == undefined) {
      this._isDali = newValue;
    }

    this.changed("isDali");
  }

  /////////////////////////////////////////////////////////////////////

  getApplicationProgram() {
    return this.template?.knxProductData.getApplicationProgramIntern(this.applicationProgramId);
  }

  /** Uses stored template baseProps when present, otherwise derives product values on access. */
  get baseProps(): BaseDeviceProps {
    if (!this.template) {
      if (this.isButton) {
        return {
          buttonBaseProps: {
            type: "Simple",
            statusLeds: 0,
            hasDisplay: false,
            supportsSplitRocker: false,
            supportsLongPress: 0,
            numOfPages: 1,
            supportsSlap: 0,
            horizontal: true,
            color: "white",
            heightMm: 90,
            widthMm: 90,
            numberOfButtonRows: 3,
          },
        };
      }
      if (this.isActor) {
        return {
          actorBaseProps: {
            channels: 8,
            deviceType: DeviceType.Generic,
          },
        };
      }

      return {};
    }

    if (this.template.baseProps) {
      return createBaseDeviceProps(this.template.baseProps);
    }
    if (this.template.genericParams) {
      return createBaseDeviceProps(this.template.genericParams);
    } else {
      if (this.isButton) {
        //
        // Bestimme die Anzahl der Button-Reihen basierend auf den vorhandenen Kanälen:
        //
        // Ermittelt die maximale Zeilennummer aus den Kanal-Tags, um die Anzahl der Button-Reihen zu bestimmen.
        //
        let buttonRows = 1;
        if (this.template.knxProductData.hasScript_setRockerConfig) {
          buttonRows = this.channels.asArray().reduce((maxRow, channel) => {
            const match = /^T(\d+)(?:[RL].*)?$/.exec(channel.tag);
            return match ? Math.max(maxRow, Number(match[1])) : maxRow;
          }, 0);
        } else {
          buttonRows = this.channels.filter(
            (channel) => channel.tag.startsWith("T") && !channel.tag.startsWith("TX"),
          ).length;
        }

        ////////////////////////////////////////////////////////////////////////////////
        const group = this.template.parent as TemplateGroup;
        const buttonTypes: Record<string, ButtonType> = {
          simple: "Simple",
          touch: "Touch",
          panel: "Panel",
        };
        const buttonType = buttonTypes[group?.tag] ?? "Simple";
        const horizontal = "Simple" === buttonType;

        let numOfPages = 1;
        if (buttonRows > 4) {
          buttonRows = Math.ceil(buttonRows / 2);
          numOfPages = 2;
        }

        return {
          buttonBaseProps: {
            type: buttonType,
            statusLeds: 0,
            hasDisplay: false,
            supportsSplitRocker: this.template?.knxProductData?.hasScript_setRockerConfig ?? false,
            supportsLongPress: 0,
            numOfPages: numOfPages,
            supportsSlap: this.channels.getItemByTag("TP") != undefined ? 1 : 0,
            horizontal: horizontal,
            color: "white",
            heightMm: 90,
            widthMm: 90,
            numberOfButtonRows: buttonRows,
          },
        };
      }
      if (this.isActor) {
        return {
          actorBaseProps: {
            channels: 8,
            deviceType: DeviceType.Generic,
          },
        };
      }
    }
    return {};
  }

  get frontImage(): string | undefined {
    return this.template?.getFrontImage(this);
  }

  get template(): DeviceTemplate | undefined {
    // todo cache if supply and template is set
    // channelTypeinfo noch nicht da und this.channelType endlosschleife
    if (
      (this._channelType == ChannelType.suply12V ||
        this._channelType == ChannelType.suply24V ||
        this._channelType == ChannelType.suply230V ||
        this._channelType == ChannelType.suply400V) &&
      this.templateId != ""
    ) {
      // update channeltype
      this.channelType = ChannelType.Unknown;
    }
    return this.state.objectRegistry.getObject(this.templateId);
  }

  set template(newTemplate: DeviceTemplate | undefined) {
    if (this.templateId != newTemplate?.id) {
      this.setTemplate(newTemplate); /// BAD !!!! ASYNC !!!!!
    }
  }

  async setTemplate(newTemplate: DeviceTemplate | undefined) {
    if (newTemplate) {
      if (this.templateId != newTemplate.id) {
        ////////////////////////////////////////////////////////////////////////////////////
        // load defaults from Template
        //  todo: handle undo
        //

        this.templateId = newTemplate.id;
        const newKnxInstanceData = new KnxInstanceData(null, this);
        this.knxInstanceData = newKnxInstanceData;

        await newTemplate.knxProductData.loadXmlData(this);

        this.knxInstanceData.loadDefaultsFromConfig();
        if (newTemplate.knxProductData.config.channelType) {
          this.channelType = newTemplate.knxProductData.config.channelType;
        }

        await this.updateAllFromKnxMetadataImmediately();

        if (newTemplate.knxProductData) {
          this.applyCustomizingScripts();
        }

        //
        // Geräte ohne AplikationsProgramm sind sowas wie einfache Spannungsversorgungrn
        // und haben KEINE physische Adresse
        //

        if (
          this.template &&
          this.template.knxProductData.getApplicationProgramIntern(this.applicationProgramId) == undefined
        ) {
          this.physicalAddress = "-";
        } else if (this.physicalAddress == "-") {
          this.findFreePhysicalAddress();
        }
        this.raiseOnChange();

        //
        // MWE 3.4.2026
        // das ist ein wirklich falsche Stelle um das Device zu öffnen !!!
        //
        // this.treeViewModel.openDevice(this.id);
      }
    } else {
      // no more template !!!

      this.templateId = "";
      // unlink all channels
      this.channels.forEach((channel) => {
        channel.mappedChannel = undefined;
        channel.knxChannelId = undefined;
      });

      // remove all knx channels
      this.knxChannels.clear();

      // check if physical address is not "-"
      if (this.physicalAddress === "-") {
        this.findFreePhysicalAddress();
      }
    }

    this.clearExtendedDatasAfterTemplateChange();
  }

  clearExtendedDatasAfterTemplateChange() {
    this.knxInstanceData.getAllExtendedDatas().forEach((entry) => {
      console.log("getAllExtendedDatas res", entry);
      if (entry.extData) {
        entry.extData.access?.delete;
        entry.extData.scene?.delete;
        entry.extData.info?.delete;
        entry.extData.channelTypeProperty?.delete;
        entry.extData.mem?.delete;
      }

      this.knxInstanceData.setExtendedData(entry.extData, entry.extData.id, entry.extData.instanceId);
    });
  }

  getPersistentProperties(): string[] {
    return [
      "channels", // no itx
      "knxChannels", // no itx
      "cabinetPlacement", // no itx
      "templateId", // no itx
      "physicalAddress", // no itx
      "_areaAddress", // no itx
      "_lineAddress", // no itx
      "etsId", // no itx
      "oref:knxInstanceData", // no itx
      // hardcocde "name" for now
      // "channelMethod",
      "_installChecks", // no itx1
      "applicationProgramId", // no itx
      "_classification", // no itx
      "eV", // no itx lastes "EvalVersion"

      ...super.getPersistentProperties(),
    ];
  }

  expandedChapterIds = new Set<string>();

  //
  // For Product Admins
  //
  configTemplate?: boolean = false;

  assertChannelsAreRequestChannels() {
    this.channels.asArray().forEach((channel) => {
      if (!channel.isRequestChannel) {
        logger.log(`Channel is not a requestChannel: ${channel.fullTag} ${channel.name}`);
        channel.isRequestChannel = true;

        const knxChannel = channel.knxChannel;
        if (knxChannel) {
          if (knxChannel.isRequestChannel) {
            logger.log(`mappedChannel is not a knxChannel: ${knxChannel.fullTag} ${knxChannel.name}`);
            knxChannel.isRequestChannel = false;
          }
        }
      }
    });

    this.knxChannels.asArray().forEach((knxChannel) => {
      if (knxChannel) {
        if (knxChannel.isRequestChannel) {
          logger.log(`mappedChannel is not a knxChannel: ${knxChannel.fullTag} ${knxChannel.name}`);
          knxChannel.isRequestChannel = false;
        }
      }
    });
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    if (this.className == "Actor") {
      return (this.parent as Cabinet)?.actors.sortBy("tag");
    } else if (this.className == "Button") {
      if (this.isSensor) {
        return (this.parent as Room)?.buttons.sortBy("tag").filter((b) => b.isSensor);
      } else {
        return (this.parent as Room)?.buttons.sortBy("tag").filter((b) => !b.isSensor);
      }
    }
    return undefined;
  }

  updateSignalsForOneChannel(knxChannel: Channel, requestChannel: Channel) {
    //
    // Create and update RequestSignals from KnxChannels
    //
    knxChannel.signals.forEach((signal) => {
      let requestSignal = signal.requestSignal;

      productionAssert(requestSignal !== undefined, _FILENAME_, 507);

      if (!requestSignal) {
        requestSignal = new Signal({}, requestChannel);
        requestChannel.signals.addWithoutUpdate(requestSignal);
      }

      requestSignal.tag = signal.tag;
      requestSignal.instanceId = signal.instanceId;
      requestSignal._name = signal._name;
      requestSignal.type = signal.type;
      requestSignal.number = signal.number;
      requestSignal.knxId = signal.knxId;
      requestSignal.knxFunctionText = signal.knxFunctionText;
      requestSignal.knxDatapointType = signal.knxDatapointType;
    });

    //
    // Delete all requestSignals that are no longer in the knxChannel
    //

    requestChannel.signals.asArray().forEach((requestSignal) => {
      if (!knxChannel.signals.findFirst((signal) => signal.number == requestSignal.number)) {
        requestChannel.signals.remove(requestSignal);
        requestSignal.delete();
      }
    });
  }

  updateSignalsFromKnxChannels() {
    this.knxChannels.forEach((knxChannel) => {
      const requestChannel = knxChannel.requestChannel;
      if (requestChannel) {
        this.updateSignalsForOneChannel(knxChannel, requestChannel);
      }
    });
  }

  moveSignalsForVirtualRockers(newChannels: Array<Channel>) {
    if (this.isButton && !this.isSensor && !this.isDecentralActor) {
      newChannels.forEach((channel) => {
        if (channel.isButtonChannel) {
          const buttonChannel = channel as ButtonChannel;
          if (buttonChannel.isVirtualRocker) {
            const rightButtonChannel = buttonChannel.rightButtonOfVirtualRocker;
            if (rightButtonChannel) {
              const rightButtonSignals = rightButtonChannel.signals.asArray();
              rightButtonSignals.forEach((rightButtonSignal) => {
                rightButtonSignal.parent = buttonChannel;
                buttonChannel.signals.addWithoutUpdate(rightButtonSignal);
                rightButtonChannel.signals.removeWithoutUpdate(rightButtonSignal);
              });
            }
          }
        }
      });
    }
  }

  assertChannelsAreButtonChannels() {
    //
    // check all channels are of the correct Class
    //
    const channelClassname = this.isButton ? "ButtonChannel" : "Channel"; // no itx

    this.channels.asArray().forEach((channel) => {
      if (channel.className != channelClassname) {
        logger.error(
          `${channel.className} is not a ${channelClassname} device:${this.tag} ${this.name} channel: ${channel.name}`,
        );
        const data = channel.toJSON();
        delete data.className;
        //
        state.objectRegistry.removeObject(channel);
        this.channels.removeWithoutUpdate(channel);
        //

        const newChannel =
          channelClassname == "ButtonChannel"
            ? new ButtonChannel({ isRequestChannel: data.isRequestChannel }, this)
            : new Channel({ isRequestChannel: data.isRequestChannel }, this);
        newChannel.fromJSON(data, this);

        newChannel.id = channel.id;
        newChannel.signals = channel.signals;
        newChannel.knxSceneInfos = channel.knxSceneInfos;
        newChannel.pageUsages = channel.pageUsages;
        //
        this.channels.addWithoutUpdate(newChannel);
      }
    });
  }

  async loadFromEstadaStorage(
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> {
    // create instances of optional references

    const orefInstancedata = data["oref:knxInstanceData"]; // no itx
    if (orefInstancedata) {
      this.knxInstanceData = new KnxInstanceData({ id: orefInstancedata }, this);
    }

    // normal loading

    const res = await super.loadFromEstadaStorage(data, parent, loadData);

    //
    // check all channels are ButtonChannels
    //
    this.assertChannelsAreButtonChannels();
    this.assertChannelsAreRequestChannels();

    //
    //constistens test for product
    //

    return res;
  }

  updateAllFromKnxMetadataIsRunning = false;
  updateTimeout: any = undefined;
  private updateAllFromKnxMetadataPromise: Promise<boolean> | undefined;
  private resolveUpdateAllFromKnxMetadata: ((result: boolean) => void) | undefined;

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    super.raiseOnChange(changedProperty, eventSource, uiOnly);
    // if (eventSource == "undo" || eventSource == "redo") {
    //   logger.log(`undo, redo ${changedProperty}`);
    //   this.updateAllFromKnxMetadata();
    // }

    if (changedProperty === "connection") {
      this.updateAllFromKnxMetadata();
    }
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    let res = new Array<BaseObject>();

    // delete all child channels
    res = res.concat(this.channels.deleteAllItems());
    res = res.concat(this.knxChannels.deleteAllItems());

    res = res.concat(super.delete());

    return res;
  }

  mergeChannelsWithSameKnxChannel() {
    let channelsWithSameKnxChannel = new Array<Channel>();
    const lastKnxChannelId = "";
    this.channels.sortBy("knxChannelId").forEach((channel) => {
      if (lastKnxChannelId != channel.knxChannel?.name) {
        ///////////
        //merge channels
        if (channelsWithSameKnxChannel.length > 1) {
          console.log(
            `${channelsWithSameKnxChannel.length} Channels with same knxChannel`,
            channelsWithSameKnxChannel[0],
            channelsWithSameKnxChannel,
          );
          showToast({
            text: `${channelsWithSameKnxChannel.length} Channels with same knxChannel ${channelsWithSameKnxChannel[0].knxChannelId}`,
          });
        }

        /////////////////////////////////////////////////////
        channelsWithSameKnxChannel = new Array<Channel>();
      }
      channelsWithSameKnxChannel.push(channel);
    });

    // if (channelsWithThisKnxChannel.length > 1) {
    //   //// merge channels here
    //   console.log(
    //     `${channelsWithThisKnxChannel.length} Channels with same knxChannel`,
    //     channel.knxChannel,
    //     channelsWithThisKnxChannel,
    //   );
    //   showToast({ text: `${channelsWithThisKnxChannel.length} Channels with same knxChannel ${knxChannel.id}` });
    // }
  }

  canBeDeletedInteractive(): boolean {
    if (this.getAllConnectionsFromChannels().length == 0) {
      return true;
    } else {
      return false;
    }
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const p = [this.fullTag, this.getAllConnectionsFromChannels().length];
      return state.translate("Device.T1StillHasT2ConnectedChannels", "T1 hat noch T2 verbundene Kanäle", {
        T1: `${p[0]}`,
        T2: `${p[1]}`,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }

  updateConnectionEndpoints() {
    this.channels.forEach((channel) => {
      channel.updateConnectionEndpoints();
    });
  }

  ///////////////////////////////////////////////////////////////
  get channelGeneral(): Channel {
    let res: Channel | undefined = undefined;

    this.channels.forEach((channel) => {
      if (channel.channelType == ChannelType.General) {
        res = channel;
      }
    });
    if (res == undefined) {
      res = new Channel({ channelType: ChannelType.General, name: "General" }, this);
      this.channels.addWithoutUpdate(res);
    }

    return res;
  }

  //////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // collapseMode
  // -----------------------------------------------------------------------------
  protected _collapseMode = 1;

  // get collapseMode(): number {
  //   var someChannelIsSelected = false;
  //   if (this.className == "Button") {
  //     someChannelIsSelected = (this as any as Button)
  //       .someChannelOrConnectionIsSelected;
  //   }

  //   if (this.selected || someChannelIsSelected) {
  //     return 2;
  //   } else {
  //     return this._collapseMode;
  //   }
  // }

  get collapseMode(): number {
    if (channelTypeInfo(this.channelType).isSupply) {
      return 0;
    } else {
      return this._collapseMode;
    }
  }

  set collapseMode(newValue: number) {
    if (this._collapseMode != newValue) {
      this._collapseMode = newValue;
      this.raiseOnChange("collapseMode");

      this.updateConnectionEndpoints();
    }
  }

  // -----------------------------------------------------------------------------

  //////////////////////////////////////////////////////////////////////

  connectionsAreHighlighted?: boolean = false;
  connectionsAreHovered?: boolean = false;

  highLightConnections(hover: boolean, type: ConnectionType | undefined) {
    if (hover) {
      this.connectionsAreHovered = true;
    } else {
      this.connectionsAreHighlighted = true;
    }

    this.channels.forEach((channel) => {
      channel.highLightConnections(hover, type, this.id);
    });
  }

  dehighLightConnections(hover: boolean) {
    if (this.connectionsAreHighlighted || this.connectionsAreHovered) {
      this.channels.forEach((channel) => {
        channel.dehighLightConnections(hover);
      });
      if (hover) {
        this.connectionsAreHovered = false;
      } else {
        this.connectionsAreHighlighted = false;
      }
    }
  }

  applyFuctionNamesToKnxParameters() {
    const knxChannelDescriptionPropertiesAlreadySet = new Set<string>();

    this.knxChannels.forEach((knxChannel) => {
      if (knxChannel.knxChannelDescriptionProperty) {
        const xid =
          knxChannel.knxChannelDescriptionProperty.id + "-" + knxChannel.knxChannelDescriptionProperty.instanceId;

        //
        // if for some reson / error two channels hve the same knxChannelDescriptionProperty
        // both channel set this value and toggle the value between two states.
        // As a resulute device.updateAllFromKnxMetadata runs endless
        //
        // even if this endless loop is fixed inside updateAllFromKnx
        //
        if (!knxChannelDescriptionPropertiesAlreadySet.has(xid)) {
          knxChannelDescriptionPropertiesAlreadySet.add(xid);
          knxChannel.applyFuctionNamesToKnxParameters();
        }
      }
    });
  }

  applyScripts() {
    this.channels.forEach((channel) => {
      channel.applyScripts();
    });
  }
  //
  // remove empty channels
  //
  removeEmptyKnxChannels(knxChannels: Array<Channel>, phase: number) {
    for (let i = 0; i < knxChannels.length; i++) {
      const channel = knxChannels[i];

      let keepChannel =
        channel.signals.size > 0 ||
        (channel.rawSceneData && channel.rawSceneData.length > 0) ||
        channel.knxChannelTypeProperty ||
        !channel.knxSceneInfos.empty;

      //
      // MWE 22.4.2026
      // für Buttons dürfen wir Channel nur mit Dummy Signal nicht löschen
      // sonst verlieren wir "disabled channels" um dieses in der ButtonList zu aktivieren.
      // Dafür sind die DummySignale in KnxParameterViewer ja genau eingefügt worden!!!!
      //
      if (!this.isButton && phase >= 2 && keepChannel) {
        const nonDummySignal = channel.signals.findFirst((signal) => signal.name.indexOf("DUMMY") < 0);
        if (!nonDummySignal) {
          //
          // channel has only dummy signals
          // so we remove it
          //
          keepChannel = false;
        }
      }

      if (!keepChannel) {
        knxChannels.splice(i, 1);
        i--;
      }
    }
  }

  removeUnusedChannels_WithoutKnxChannel_And_WithoutConnections() {
    // Generic request channels are defined by the template, even while their
    // synthetic KNX mappings are being created or restored.
    if (this.template?.genericParams) return;
    if (this.updateAllFromKnxMetadataOnceDone) {
      logger.error("removeUnusedChannels_WithoutKnxChannel_And_WithoutConnections for device SKIPPED", this.fullTag);
      return;
    }

    this.channels.asArray().forEach((channel) => {
      if (
        !this.shouldPreserveUnusedChannel(channel) &&
        !channel.knxChannel &&
        channel.connections.size == 0 &&
        channel.allGroupAddressesOfChannel().length == 0
      ) {
        channel.delete();
      }
    });
  }

  /** Keeps C1-owned button functions stable while template views inspect the device. */
  shouldPreserveUnusedChannel(channel: Channel): boolean {
    if (this.template?.genericParams && channel.isRequestChannel) return true;
    if (!channel.isButtonChannel) return false;
    return Boolean((this.isButton && this.c1) || channel.c1);
  }

  // static moveSceneInfosBackward(channels: Array<Channel>) {
  //   for (let i = channels.length - 1; i > 0; i--) {
  //     const channelI = channels[i];
  //     if (
  //       channelI.knxSceneInfos.knxSceneInfos.length > 0 &&
  //       channelI.signals.size == 0
  //     ) {
  //       for (let j = i - 1; j >= 0; j--) {
  //         const channelJ = channels[j];
  //         if (
  //           channelJ.channelType != ChannelType.Unknown &&
  //           channelJ.channelType != ChannelType.General &&
  //           channelJ.knxSceneInfos.knxSceneInfos.length == 0
  //         ) {
  //           channelJ.knxSceneInfos.knxSceneInfos =
  //             channelI.knxSceneInfos.knxSceneInfos;
  //           break; // break inner for loop (j)
  //         }
  //       }
  //     }
  //   }
  // }

  static DebugOutputChannels(s: string, channels: Array<Channel>, indent: string = "") {
    if (!isDevelopmentEnvironment()) {
      return;
    }

    logger.log(`${indent} DebugOutputChannels ${s} number of channels=${channels.length}`);

    channels.forEach((channel) => {
      channel.signals.forEach((signal) => {
        if (signal.parent != channel) {
          logger.error(`${s} parent inconsistent`, signal, channel);
          signal.parent = channel;
        }
      });
    });

    return;

    channels.forEach((channel) => {
      // prettier-ignore
      logger.log(`${indent}    ${channel.name} #signals:${channel.signals.size} #SceneInfos:${channel.knxSceneInfos.knxSceneInfos.length} #rawSceneData:${channel.rawSceneData?.length}  ${channel.id} `);

      channel.assetIsKnxChannel();

      if (channel.subChannels && channel.subChannels.length > 0) {
        Device.DebugOutputChannels(`${s}-${channel.name} >> subchannels`, channel.subChannels, indent + "  ");
      }

      const printSignals = true;

      if (printSignals) {
        if (channel.signals.size > 0) {
          logger.log(`    Signals ${channel.signals.size}`);
          channel.signals.forEach((signal) => {
            logger.log(`${indent}      ${signal.number}: ${signal.name} ${signal.knxFunctionText}`);
          });
        }
      }
    });
  }

  async getModelMetadata(): Promise<any | undefined> {
    const knxProductData = this.template?.knxProductData;
    if (knxProductData) {
      const data = await knxProductData.getModelMetadata(this);
      return data;
    }
    return undefined;
  }

  async buildContext() {
    await this.getModelMetadata();
    return this;
  }

  onChannelConnect(channel: Channel, connection: Connection): void {}

  updateAllFromKnxMetadataOnceDone = false;
  async updateAllFromKnxMetadataOnce(): Promise<boolean> {
    if (!this.updateAllFromKnxMetadataOnceDone) {
      this.updateAllFromKnxMetadataOnceDone = true;
      return this.updateAllFromKnxMetadata();
    }
    return true;
  }

  //
  // createGenericChannels erzeugt oder löscht requestChannels des devices aufgrund der daten in template.genericParams
  // bestehede Kanäle werden nicht angetastet, es sei den sie müssen gelöscht werden, das die Anzahl der Channels reduziert wurde
  //
  createGenericChannels() {
    const params = this.template?.genericParams;
    if (!params) return;
    const isButton = this.template!.genericProductType === "Button";
    const isSensor = this.template!.genericProductType === "Sensor";
    const isActor =
      this.template!.genericProductType === "Actor" || this.template!.genericProductType === "DecentralActor";
    const channels = isSensor
      ? 1
      : isButton
        ? params.buttonBaseProps?.numberOfButtonRows
        : params.actorBaseProps?.channels;
    if (channels === undefined || !Number.isSafeInteger(channels) || channels < 1) return;
    const pages = isButton ? (params.buttonBaseProps?.numOfPages ?? 1) : 1;
    const count = channels * pages;
    if (!Number.isSafeInteger(pages) || pages < 1 || !Number.isSafeInteger(count)) return;

    const existing = this.channels.asArray();
    const existingByTag = new Map(existing.map((channel) => [channel.tag, channel]));
    const expected = new Set<string>();
    for (let number = 1; number <= count; number++) {
      const tag = `${isButton || isSensor ? "T" : ""}${String(number).padStart(2, "0")}`;
      // Keep an existing shared/split rocker and its connections unchanged.
      const row = [tag, `${tag}L`, `${tag}R`].flatMap((key) => {
        const channel = existingByTag.get(key);
        return channel ? [channel] : [];
      });
      if (row.length) {
        row.forEach((channel) => expected.add(channel.tag));
        if (row.some((channel) => channel.tag === `${tag}L` || channel.tag === `${tag}R`)) {
          expected.add(`${tag}L`);
          expected.add(`${tag}R`);
        }
      } else if (
        isButton &&
        params.buttonBaseProps!.supportsSplitRocker &&
        (this as unknown as Button).rockerConfigs?.[number] !== RockerConfig.rocker
      ) {
        expected.add(`${tag}L`);
        expected.add(`${tag}R`);
      } else {
        expected.add(tag);
      }
      if (isButton && params.buttonBaseProps!.supportsSplitRocker) {
        // Generic products have no KNX script to initialize the persisted row configuration.
        const split = expected.has(`${tag}L`) || expected.has(`${tag}R`);
        (this as unknown as Button).setRockerConfig(number, split ? RockerConfig.single : RockerConfig.rocker);
      }
    }
    if (isButton) {
      for (const tag of [...expected]) {
        if ((params.buttonBaseProps!.supportsLongPress ?? 0) > 0) expected.add(`${tag}_l`);
        if (
          params.buttonBaseProps!.supportsLongPress === 2 ||
          ((params.buttonBaseProps!.supportsLongPress ?? 0) > 0 && params.buttonBaseProps!.supportsSlap === 2)
        )
          expected.add(`${tag}_xl`);
      }
    }
    if (isButton && params.buttonBaseProps!.supportsSlap > 0) expected.add("TP");
    if (isButton && params.buttonBaseProps!.supportsSlap === 2) expected.add("TP_l");

    let changed = false;
    for (const channel of existing) {
      if (expected.has(channel.tag)) {
        expected.delete(channel.tag);
        continue;
      }
      const numbered = isButton ? /^T(\d+)(?:L|R)?(?:_l|_x|_xl)?$/.exec(channel.tag) : /^(\d+)$/.exec(channel.tag);
      const removed = numbered && Number(numbered[1]) > count;
      const removedSlap = isButton && (channel.tag === "TP" || channel.tag === "TP_l");
      if (channel.isRequestChannel && (removed || removedSlap)) {
        channel.delete();
        changed = true;
      }
    }
    const actorType = params.actorBaseProps?.deviceType;
    const channelType = isSensor
      ? ChannelType.Switch
      : isActor && actorType && actorType in ChannelType
        ? (actorType as unknown as ChannelType)
        : ChannelType.Generic;
    const defaultChannelName = (tag: string): string => {
      if (isSensor && tag === "T01") return itx("genericDevice.sensorAction", "Sensor Aktion");
      if (tag === "TP") return itx("genericDevice.slapChannel", "Patsch");
      if (tag === "TP_l") return itx("genericDevice.slapLongChannel", "Patsch lang");
      const match = /^T?(\d+)(L|R)?$/.exec(tag);
      if (!match) return tag;
      const number = Number(match[1]);
      const base = isButton ? itx("genericDevice.buttonChannel", "Taste") : itx("genericDevice.actorChannel", "Kanal");
      const side =
        match[2] === "L"
          ? ` ${itx("genericDevice.left", "links")}`
          : match[2] === "R"
            ? ` ${itx("genericDevice.right", "rechts")}`
            : "";
      return `${base} ${number}${side}`;
    };
    for (const tag of expected) {
      const ChannelClass = isActor ? Channel : ButtonChannel;
      const channel = new ChannelClass(
        { tag, name: isSensor ? defaultChannelName(tag) : "", channelType, isRequestChannel: true },
        this,
      );
      this.channels.add(channel);
      changed = true;
    }
    // Generic products still need the same bidirectional request/KNX mapping
    // as catalog products. Reuse channel objects so reevaluation preserves IDs.
    const requestChannels = this.channels.asArray().filter((channel) => channel.isRequestChannel && !channel.isDeleted);
    const requestIds = new Set(requestChannels.map((channel) => channel.id));
    const unusedKnxChannels = new Set(this.knxChannels.asArray());
    for (const requestChannel of requestChannels) {
      const mapped = requestChannel.mappedChannel;
      let knxChannel = mapped && unusedKnxChannels.has(mapped) && !mapped.isDeleted ? mapped : undefined;
      knxChannel ??= Array.from(unusedKnxChannels).find(
        (channel) =>
          !channel.isDeleted &&
          channel.tag === requestChannel.tag &&
          (!channel.mappedId || channel.mappedId === requestChannel.id || !requestIds.has(channel.mappedId)),
      );
      if (!knxChannel) {
        const ChannelClass = requestChannel.isButtonChannel ? ButtonChannel : Channel;
        knxChannel = new ChannelClass(
          {
            tag: requestChannel.tag,
            name: defaultChannelName(requestChannel.tag),
            channelType: requestChannel.channelType,
            isRequestChannel: false,
          },
          this,
        );
        this.knxChannels.add(knxChannel);
        changed = true;
      }
      unusedKnxChannels.delete(knxChannel);
      if (!knxChannel.name?.trim()) {
        knxChannel.name = defaultChannelName(requestChannel.tag);
        changed = true;
      }
      if (knxChannel.tag !== requestChannel.tag || knxChannel.channelType !== requestChannel.channelType) {
        knxChannel.tag = requestChannel.tag;
        knxChannel.channelType = requestChannel.channelType;
        changed = true;
      }
      if (requestChannel.mappedId !== knxChannel.id || knxChannel.mappedId !== requestChannel.id) {
        requestChannel.mappedChannel = undefined;
        knxChannel.mappedChannel = undefined;
        requestChannel.mappedChannel = knxChannel;
        changed = true;
      }
    }
    for (const knxChannel of unusedKnxChannels) {
      knxChannel.mappedChannel = undefined;
      knxChannel.mappedId = undefined;
      knxChannel.delete();
      if (this.knxChannels.asArray().includes(knxChannel)) this.knxChannels.remove(knxChannel);
      changed = true;
    }
    if (changed) {
      this.channels.raiseOnUpdateUi();
      this.knxChannels.raiseOnUpdateUi();
      this.raiseOnChange();
    }
  }

  updateAllFromKnxMetadata(onlyIfReEvalIsNeeded?: boolean): Promise<boolean> {
    if (this.template == undefined) return Promise.resolve(false);
    if (this.updateAllFromKnxMetadataImmediatelyIsRunning) return Promise.resolve(true);

    const updateRequired = !onlyIfReEvalIsNeeded || this.reEvalKnxMetadataNeeded;
    if (!this.updateAllFromKnxMetadataPromise && !updateRequired) return Promise.resolve(true);

    if (!this.updateAllFromKnxMetadataPromise) {
      this.updateAllFromKnxMetadataPromise = new Promise<boolean>((resolve) => {
        this.resolveUpdateAllFromKnxMetadata = resolve;
      });
      void showGlobalWaitIndicator(true);
    }

    // Calls made by the recalculation itself used to be ignored by the
    // recursion guard. They share the running promise but must not schedule an
    // endless follow-up recalculation. User interaction is blocked while the
    // promise is pending, so external changes cannot race this calculation.
    if (updateRequired && !this.updateAllFromKnxMetadataIsRunning) {
      this.scheduleUpdateAllFromKnxMetadata();
    }

    return state.undoManager.trackAsyncWork(this.updateAllFromKnxMetadataPromise);
  }

  private scheduleUpdateAllFromKnxMetadata() {
    if (this.updateTimeout) clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(() => void this.runScheduledUpdateAllFromKnxMetadata(), 100);
  }

  private async runScheduledUpdateAllFromKnxMetadata() {
    this.updateTimeout = undefined;
    this.updateAllFromKnxMetadataIsRunning = true;
    let successful = true;

    try {
      await this.updateAllFromKnxMetadataImmediately();
    } catch (ex) {
      successful = false;
      logger.error(ex);
    } finally {
      this.updateAllFromKnxMetadataIsRunning = false;
      this.completeScheduledUpdateAllFromKnxMetadata(successful);
    }
  }

  private completeScheduledUpdateAllFromKnxMetadata(successful: boolean) {
    if (!this.updateAllFromKnxMetadataPromise) return;

    const resolve = this.resolveUpdateAllFromKnxMetadata;
    this.resolveUpdateAllFromKnxMetadata = undefined;
    this.updateAllFromKnxMetadataPromise = undefined;
    resolve?.(successful);

    void showGlobalWaitIndicator(false, undefined, true);
  }

  private updateAllFromKnxMetadataImmediatelyIsRunning = false;

  public async updateAllFromKnxMetadataImmediately() {
    if (this.updateAllFromKnxMetadataImmediatelyIsRunning) {
      logger.log("recursive call to updateAllFromKnxMetadataImmediately, skipped");
      return;
    }
    this.updateAllFromKnxMetadataImmediatelyIsRunning = true;

    // A direct immediate update supersedes a debounced update. It adopts and
    // completes the already registered promise instead of leaving the undo
    // step permanently blocked by a cancelled timer.
    const adoptsScheduledUpdate = Boolean(this.updateAllFromKnxMetadataPromise && this.updateTimeout);
    if (adoptsScheduledUpdate) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = undefined;
    }

    let successful = true;
    try {
      await this.executeUpdateAllFromKnxMetadataImmediately();
    } catch (ex) {
      successful = false;
      throw ex;
    } finally {
      this.updateAllFromKnxMetadataImmediatelyIsRunning = false;
      if (adoptsScheduledUpdate) this.completeScheduledUpdateAllFromKnxMetadata(successful);
    }
  }

  private async executeUpdateAllFromKnxMetadataImmediately() {
    // calculate ComObjects

    const templateName = this.template?.name || "";

    await EP(
      templateName + "-updateAllFromKnx", // no itx
    ).runAsync(async () => {
      logger.log("updateAllFromKnxMetadata");

      //
      // reset script output for debugging
      //
      state.scriptOutput.value = "";

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = undefined;
      }
      this.channels.forEach((channel) => {
        channel.existsBeforeThisEvaluation = true;
      });

      this.reEvalKnxMetadataNeeded = false;

      const knxProductData = this.template?.knxProductData;
      if (knxProductData) {
        try {
          if (this.template?.genericParams) {
            this.createGenericChannels();
          } else {
            await knxProductData.createRefMap(this);
            this.reEvalCount = 0;

            let maxReEvalCount = 5;

            this.knxInstanceData.savePreviousValues();

            this.knxInstanceData.clearAllValueLevelsUpTo(ValueLevels.deleteAfterTemplateChange);
            this.knxInstanceData.clearReEvalCounts();
            this.knxInstanceData.clearReferenced();

            do {
              let newChannels = new Array<Channel>();
              let newSignals = new Array<Signal>();

              this.reEvalKnxMetadataNeeded = false;

              let postponeApplyScripts = false;

              if (isUiModeProductAdmin() || !this.knxParameterViewerHasRundOnce) {
                this.definitions = this.template?.knxProductData.currentRules?.definitions(this);
              }

              if (isUiModeProductAdmin() && !this.knxParameterViewerHasRundOnce) {
                //
                // Für ProductAdmins wird das setzen von Properties in Script dahingehende überwacht,
                // ob die zu setzende Property schon existiert.
                // Wenn nich erfolgt eine Fehlermeldung.
                //
                // wenn "KnxParameterViewer" nicht mindestens einmal ausgeführt wurde
                // sind Parameter die bislang nur als DefaultParameter gesetzt werden noch nicht vorhanden.
                // und würden zu diesem Zeitpunkt zu unberechtigten Fehlermeldungen führen.
                //
                // Daher überspringen wir hier das ausführen der Scripts
                // und setzen gleichzeitig "this.reEvalKnxMetadataNeeded".
                // Dadurch erzwingen wir einen zweiten Durchlauf be dem dann die DefaultParameter gesetzt sind.
                // und die Scripts hier ausgeführt werden
                //
                postponeApplyScripts = true;
                maxReEvalCount++;
              }

              if (postponeApplyScripts) {
                this.reEvalKnxMetadataNeeded = true;
              } else {
                EP(
                  templateName + "-ApplyScripts", // no itx
                ).run(() => {
                  //
                  // da diese Scrips nur PropertyValues setzen,
                  // können VOR der evaluation des LKnxParameterViewers ausgeführt werden.
                  //
                  // AUf diese weise stehen die entsperechenden Properies schon auf den richtigen Werten;
                  // können entsprechenden Kanäle und Signale aktivieren und reduzieren so die ANzahl der nötigen "ReEvals"
                  //
                  this.applyScripts();
                });
              }

              await EP(
                templateName + "-KnxParameterViewer", // no itx
              ).runAsync(async () => {
                const viewer = await KnxParameterViewer({
                  device: await this.buildContext(),
                  isActor: this.isActor,
                  xchannels: newChannels,
                  xSignals: newSignals,
                  isLandscape: Browser.isLandScape(),
                });
                this.knxParameterViewer.value = createKnxParameterViewerCache(viewer, window);
                this.knxParameterViewerHasRundOnce = true;
              });

              newSignals = this.handleSceneDataSignals(newSignals);

              //
              // pretranslate German texts for signal detection into English
              //

              if (this.template?.knxProductData && !this.template.knxProductData.hasTranslationEN) {
                console.log(
                  "language mapping EN not found, physeudo translate German texts for signal detection  default-Langugage=",
                  this.template.knxProductData.productInfo.DefaultLanguage,
                );

                const importModule = (await import("../KnxMappings/pretranslateSignalDetectionTexts")) as any;

                const { preTranslateToEnglish } = importModule;
                let defaultLanguage = this.template.knxProductData.productInfo.DefaultLanguage || "DE-de"; // no itx

                defaultLanguage = defaultLanguage.split("-")[0].toLowerCase();

                newSignals.forEach((signal) => {
                  signal.knxFunctionTextEN = preTranslateToEnglish(defaultLanguage, signal.knxFunctionText);
                  signal.knxTextENForDetection_Template = preTranslateToEnglish(
                    defaultLanguage,
                    signal.knxTextENForDetection_Template,
                  );
                  signal.knxTextENForDetectionRaw = preTranslateToEnglish(
                    defaultLanguage,
                    signal.knxTextENForDetectionRaw,
                  );
                  signal.knxTextEN = preTranslateToEnglish(defaultLanguage, signal.knxTextEN);
                });
              }

              // function collectRawSceneData(channel: Channel): RawSceneDataItem[] {
              //   const res: RawSceneDataItem[] = [];
              //   if (channel.rawSceneData) {
              //     res.push(...channel.rawSceneData);
              //   }
              //   if (channel.subChannels) {
              //     res.push(...channel.subChannels.flatMap(collectRawSceneData));
              //   }
              //   return res;
              // }

              // const totalRawSceneDatas = newChannels.flatMap(collectRawSceneData);

              Device.DebugOutputChannels("a", newChannels);

              this.removeEmptyKnxChannels(newChannels, 1);

              /////////////////////////////////////////////////////
              //
              // if we have just one channel with subChannels,
              // we move the subChannels one level up
              //
              if (newChannels.length == 1 && newChannels[0].signals.size == 0 && newChannels[0].subChannels?.length) {
                newChannels = newChannels[0].subChannels;
              }
              /////////////////////////////////////////////////////

              Device.DebugOutputChannels("a1", newChannels); // no itx

              this.checkForDuplicateSignalNumbers(newChannels);

              //
              // eleminate "dummy main channel"
              //
              if (newChannels.length == 1 && newChannels[0].subChannels && newChannels[0].subChannels?.length > 0) {
                newChannels = newChannels.concat(newChannels[0].subChannels);
                newChannels[0].subChannels = undefined;
              }
              ///

              // if (this.isActor || this.isDecentralActor) {
              //   newChannels = Device.mergeSubChannelsToMainChannelByHierachy(newChannels);

              //   Device.DebugOutputChannels("a2", newChannels); // no itx

              //   newChannels = Device.mergeSubChannelsToMainChannelByIndent(newChannels);
              // } else {
              //   newChannels = Device.mergeSubChannelsToMainChannelByHierachy(newChannels);
              // }

              Device.DebugOutputChannels("b", newChannels);

              if (this.template?.knxProductData) {
                for (const signal of newSignals) {
                  // if (signal.evalText) {
                  //   signal.evalText.eval(signal.evalText.params, 2);
                  // }

                  determineComObjectFunction(
                    this,
                    this.template?.knxProductData,
                    (this.isActor && this.deviceType != DeviceType.Lock) ||
                      (this.isButton && (this as any as Button).isDecentralActor),
                    signal,
                  );
                }
              }

              //
              // channels for ParameterViewer are not usees since some time
              // delete them here to make this situation obvious
              //
              newChannels = [];

              const ignoreChannelCalculation = this.reEvalCount < maxReEvalCount - 1 && this.reEvalKnxMetadataNeeded;
              if (ignoreChannelCalculation) {
                logger.log("ignore Channel calculation because reevalute is pending", this.reEvalCount);
              } else {
                EP(
                  templateName + "-MergeChannels", // no itx
                ).run(() => {
                  //
                  // organize signals into channels
                  //
                  if (this.isActor) {
                    // decentralActors dont have the same channel structure as actors
                    //
                    // for Actors
                    //

                    if (this.template?.knxProductData.hasScript_mapSignalToTag) {
                      newChannels = createChannelsByRule(this, this.isActor, newSignals);
                    } else {
                      newChannels = createChannelsByComObjectNames(this, newSignals);
                    }
                  } else {
                    //
                    // for Buttons
                    //

                    ///
                    /// mwe 27.11.2025 buttons haben jetzt IMMER eine Script
                    /// im ZweifelsFalle ein default-Script
                    //
                    /// mwe 27.11.2025 buttons haben jetzt IMMER eine Script  this.template?.knxProductData.hasScript_mapSignalToTag) {
                    newChannels = createChannelsByRule(this, this.isActor || this.isDecentralActor, newSignals);

                    if (this.template && !this.template?.knxProductData.hasScript_mapChannelType) {
                      //
                      // Ignore this for scripted buttons
                      //
                      newChannels = cleanUpDuplicateKnxChannelTypeProperty(newChannels);
                    }
                  }
                });

                Device.DebugOutputChannels("c", newChannels);

                //
                // Detect the types of the single channels once again, AFTER groupGeneralSignal____
                //
                determineChannelTypes(newChannels, this.isActor || this.isDecentralActor);

                // mergeEnvironmentSignals(this, newChannels, 1);

                if (!this.isActor && !this.isDecentralActor) {
                  newChannels = mergeTemperaturChannels(newChannels);
                }

                if (
                  (this.isActor || this.isDecentralActor) &&
                  (this.channelType == ChannelType.Lock ||
                    this.template?.knxProductData.productInfo.ChannelType == ChannelType.Lock)
                ) {
                  modifyChannelTypesForBinaryInput_Lock(newChannels);
                }

                const knxBuildSceneInfos = new KnxBuildSceneInfos(knxProductData);

                knxBuildSceneInfos.knxBuildSceneInfos(newChannels);

                Device.DebugOutputChannels("d", newChannels);

                this.removeEmptyKnxChannels(newChannels, 2);

                Device.DebugOutputChannels("e", newChannels);

                if (newChannels.length == 0) {
                  logger.log("error no channels found");
                }

                // //
                // // sort new channnel by signal numbers an assign this sequence as indexes to the channels
                // //
                // newChannels = sortArrayByIntProperty(newChannels, "firstSignalNumber"); // no itx

                // sort channels lexicographically by channeltypeProperty
                newChannels.sort((a: any, b: any) => {
                  let ai = parseInt(a.firstSignalNumber);
                  let bi = parseInt(b.firstSignalNumber);

                  if ((ai == -1 || bi == -1) && a.knxChannelTypeProperty && b.knxChannelTypeProperty) {
                    ai = parseFloat(a.knxChannelTypeProperty.id.match("U?P-(\\d+)_R-(\\d+)").slice(1).join("."));
                    bi = parseFloat(b.knxChannelTypeProperty.id.match("U?P-(\\d+)_R-(\\d+)").slice(1).join("."));
                  }

                  if (ai < bi) {
                    return -1;
                  }
                  if (ai > bi) {
                    return 1;
                  }
                  return 0;
                });

                let i = 1;
                newChannels.forEach((newChannel) => {
                  if (!newChannel.tag) {
                    if (newChannel.channelType == ChannelType.General) {
                      newChannel.tag = "xG"; // no itx
                    } else if (newChannel.channelType == ChannelType.Environment) {
                      newChannel.tag = TAG_ENVIRONMENT; // no itx
                    } else if (!newChannel.tag.startsWith(TAG_TX) && newChannel.channelType != ChannelType.Unknown) {
                      if (this.isDecentralActor) {
                        // do nothing like for button the tag is already set
                      } else if (this.isActor) {
                        const newChannelTag = i.toString().padStart(2, "0");
                        newChannel.tag = newChannelTag;
                      } else {
                        //
                        // for button the tag is already set
                        //
                      }
                      newChannel.index = i++;
                    }
                  }
                });

                //
                // remove mapping of old knx channels with requiement channels
                //
                this.knxChannels.forEach((knxChannel) => {
                  knxChannel.mappedChannel = undefined;
                });

                //
                // Create Environment Channel from Environment-Signals
                //
                newChannels = mergeEnvironmentSignals(this, newChannels);

                newChannels = mergeAllSpecialSiganls(this, newChannels, newSignals);

                //
                // wenn bis hier her keine Channeltype bestimmt werden konnte,
                // dann setzen wir den Channeltype auf Ignored
                //
                newChannels.forEach((newChannel) => {
                  if (newChannel.channelType == ChannelType.Unknown) {
                    newChannel.channelType = ChannelType.Ignored;
                  }
                });

                this.removeEmptyKnxChannels(newChannels, 3);
                //
                // assign newChannels to this.knxChannels
                //
                this.knxChannels.items.clear();
                newChannels.forEach((newChannel) => {
                  newChannel.assetIsKnxChannel();
                  newChannel.parent = this;
                  this.knxChannels.addWithoutUpdate(newChannel);
                });

                // this.ensureRequirementChannelsForKnxChannels(); // is intergrated in mapKnxChannelsToRequieremntChannels
                EP(
                  templateName + "-MapChannels", // no itx
                ).run(() => {
                  this.moveManualSignals();

                  // if (this.isButton) {
                  if (this.isButton) {
                    mapKnxChannelsToRequieremntChannelForButton(this as any as Button);
                  } else {
                    //
                    // map requierment channels to knxChannels
                    //
                    mapKnxChannelsToRequieremntChannelsForActor(this);
                  }

                  cleanupSignals(this);
                  // check here ? manual signals
                });

                // mergeEnvironmentSignals(this, newChannels, 1);

                // delete not self created empty not maped channels
                this.channels.forEach((channel) => {});

                determineChannelTypeSensitiveSignalTypes(newChannels, this.isActor);

                this.checkForInvalidSceneInfos(newChannels);

                //
                // set error states for channels
                //
                if (this.template) {
                  this.channels.forEach((channel) => {
                    // following ChannelTypes are not error for Users and Experts
                    const doFlagError = ![
                      ChannelType.Unknown,
                      ChannelType.Generic,
                      ChannelType.Environment,
                      ChannelType.General,
                      ChannelType.GenericValue,
                    ].includes(channel.channelType);

                    channel.flagErrors({ noMatchingKNXChannel: "" }, channel.knxChannel == undefined && doFlagError);
                  });
                }
                //
                // optimize names for all channels
                //

                this.channels.forEach((channel) => {
                  const restrictOptimization = this.isButton && channel.isActorChannel;
                  channel.optimizeName(restrictOptimization);
                });

                this.knxChannels.forEach((channel) => {
                  const restrictOptimization = this.isButton && channel.isActorChannel;
                  channel.optimizeName(restrictOptimization);
                });

                this.channels.raiseOnUpdateUi();
                this.knxChannels.raiseOnUpdateUi();
              } // !ignoreChannelCalculation

              EP(
                templateName + "-ApplyFunctionNames", // no itx
              ).run(() => {
                //
                // apply drawing informations to KnxParameters
                //
                this.applyFuctionNamesToKnxParameters();
                if (this.isButton) {
                  this.applyChannelTypeProperties();
                }
              });

              EP(
                templateName + "-ApplySceneConnections", // no itx
              ).run(() => {
                //
                // apply Scene connections to KnxParameters
                //
                this.applySceneConnectionsToKnxParameters();
              });
              //
              // if ready == false
              //     we have to repeat, because applying Sceneinformations to the KnxParameters
              //     may enable new chapters/new properties.
              //     We need these new properties to apply the scene behaviour
              //

              this.template?.knxProductData.currentRules?.pullAllValues(this, {}) || false;

              this.knxInstanceData.checkPreviousValuesForReevaluation();
              this.knxInstanceData.savePreviousValues();

              if (isUiModeProductAdmin()) {
                if (this.reEvalKnxMetadataNeeded) {
                  logger.log("reEvalKnxMetadataNeeded");
                  this.listReEvalCounts();
                }
              }
            } while (++this.reEvalCount < maxReEvalCount && this.reEvalKnxMetadataNeeded);

            if (this.reEvalCount == maxReEvalCount) {
              if (isUiModeProductAdmin()) {
                const sReEvals = this.listReEvalCounts();

                showErrToast(noItx("UpdateAllFromKnxMetadataImmediately hit reEval limit of 3.") + "\n" + sReEvals);
              }
              this.reEvalKnxMetadataNeeded = false;
              logger.error("UpdateAllFromKnxMetadataImmediately hit reEval limit of 3.");
            }
          }

          ////////////////

          this.cleanupDuplicateUnusedRequestChannels();
          this.updateRequestChannelsFromKnxChannels();

          this.checkIfRequestChannelsAreCompatibleToKnxChannels();

          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = undefined;
          }

          if (!this.isActor) {
            //
            // check all channels are ButtonChannels
            //
            this.assertChannelsAreButtonChannels();

            this.moveSignalsForVirtualRockers(this.knxChannels.asArray());

            Device.DebugOutputChannels("after move", this.knxChannels.asArray()); // no itx
          }

          this.updateSignalsFromKnxChannels();
          this.checkAllConnectionsAllowed();
        } catch (ex) {
          logger.log(`Error in updateAllFromKnxMetadata`, ex);
        }
      } else {
        // set maped channel undefinded
        let i = 0;
        let changed = false;
        this.channels.sortBy("tag").forEach((channel) => {
          if (channel?.mappedChannel) {
            channel.mappedChannel = undefined;
            channel.knxChannelId = undefined;
            channel.name = this.getChannelName(i++);
            changed = true;
          }
        });

        if (changed) {
          // remove all knx channels
          this.knxChannels.clear();
          this.channels.raiseOnChange();
          this.knxChannels.raiseOnChange();
        }
      }

      if (this.isActor || this.isDecentralActor) {
        this.determineActorType();
      }

      this.moveImportedSignalsToChannels();

      this.eV = CURRENT_EVAL_VERSION;

      logger.log("updateAllFromKnxMetadatas done");
    });

    this.localConsistencyCheck();

    this.knxParameterViewer.raiseOnChangeUI("redraw");
  }

  listReEvalCounts() {
    logger.log("list of ReEvalCounts");
    let s = "";
    this.knxInstanceData.getAllExtendedDatas().forEach((extendedData) => {
      const extReeval = extendedData.extData?.reeval;
      if (extReeval?.count) {
        s += `count:${extReeval.count} id:${extendedData.id}`;
        if (extendedData.instanceId) {
          s += ` instanceId:${extendedData.instanceId}`;
        }
        s += "\n";
      }
    });

    logger.log(s);
    return s;
  }

  checkForInvalidSceneInfos(newChannels: Array<Channel>) {
    const s = new Set<string>();
    newChannels.forEach((channel) => {
      if (channel.knxSceneInfos.channelSceneEnablingId) {
        const xId = `${channel.knxSceneInfos.channelSceneEnablingId}-${channel.knxSceneInfos.channelSceneEnablingInstanceId}`;
        if (s.has(xId)) {
          logger.error(
            `duplicate sceneInfo found, delete them: ${xId} ${channel.name} ${channel.knxSceneInfos.channelSceneEnablingId} `,
          );
          channel.knxSceneInfos = new KnxSceneInfos();
        }
        s.add(xId);
      }

      channel.knxSceneInfos.knxSceneInfos.forEach((sceneInfo) => {
        if (sceneInfo.activateId) {
          const xId = `${sceneInfo.activateId}-${sceneInfo.instanceId}`;
          if (s.has(xId)) {
            logger.error(`duplicate sceneInfo found, delete them: ${xId} ${channel.name} ${sceneInfo.activateId} `);
            channel.knxSceneInfos = new KnxSceneInfos();
          }
          s.add(xId);
        }
        if (sceneInfo.numberId) {
          const xId = `${sceneInfo.numberId}-${sceneInfo.instanceId}`;
          if (s.has(xId)) {
            logger.error(`duplicate sceneInfo found, delete them: ${xId} ${channel.name} ${sceneInfo.numberId} `);
            channel.knxSceneInfos = new KnxSceneInfos();
          }
          s.add(xId);
        }
      });
    });
  }

  applyChannelTypeProperties() {
    logger.error("applyChannelTypeProperties is only implemented for Buttons");
  }

  applyCustomizingScripts() {
    if (this.isButton && !this.isSensor && !this.isDecentralActor) {
      const button = this as any as Button;

      button.buttonChannels.forEach((buttonChannel) => {
        if (channelTypeInfo(buttonChannel.channelType).isSettableInCustomizing) {
          buttonChannel.executeSetRockerCustomizingScript();
          buttonChannel.executeSetChannelTypeCustomizingScript(button.channelType);
        }
      });
    }
  }

  checkForDuplicateSignalNumbers(channels: Channel[]) {
    // collect all signals of all channel in one array
    const allSignals = new Array<Signal>();
    channels.forEach((channel) => {
      channel.signals.forEach((signal) => allSignals.push(signal));
    });

    const sortedSignals = sortArrayByIntProperty(allSignals, "number"); // no itx

    for (let i = 0; i < sortedSignals.length; i++) {
      const signalI = sortedSignals[i];
      for (let j = i + 1; j < sortedSignals.length; j++) {
        const signalJ = sortedSignals[j];
        if (signalI.number == signalJ.number) {
          logger.log(`duplicate signal found: ${signalI.number} ${signalI.parent?.name} ${signalJ.parent?.name} `);
          (signalJ.parent as Channel).signals.removeWithoutUpdate(signalJ);
        }
      }
    }
  }

  // ensureRequirementChannelsForKnxChannels() {
  //   let count = 0;
  //   this.knxChannels.forEach((knxChannel) => {
  //     if (
  //       knxChannel.channelType != ChannelType.Unknown &&
  //       knxChannel.channelType != ChannelType.General
  //     ) {
  //       count++;
  //     }
  //   });
  //   this.modifyChannelNumber(count);
  // }

  //
  // determine the type of the actor
  // if all channels of the actor are of the same type
  //   then the actorType = the type of all channels
  //   else the acatorType = Unkown
  //

  determineActorType() {
    let multipleChannelTypesFound = false;
    let actorType = ChannelType.Unknown;
    this.knxChannels.forEach((knxChannel) => {
      if (knxChannel.channelType != ChannelType.Unknown && knxChannel.channelType != ChannelType.General) {
        if (actorType == ChannelType.Unknown) {
          actorType = knxChannel.channelType;
        } else if (knxChannel.channelType == actorType) {
          // ok, one more channel of the same type.
          // nothing to do
        } else {
          multipleChannelTypesFound = true;
        }
      }
    });
  }

  checkAllConnectionsAllowed() {
    this.channels.forEach((channel) => {
      channel.connections.forEach((con) => {
        if (con.fromDevice && con.toDevice) {
          const allowed = AllowedConections.isConnectionAllowed(con.fromDevice, con.toDevice, false);
          con.forbidden = !allowed;
        }
      });
    });
  }

  getChannelName(i: number) {
    const CHANNEL_NAMES = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return CHANNEL_NAMES.substring(i, i + 1);
  }

  modifyChannelNumber(size: number, type?: "Actor" | "Button", newChannelType?: ChannelType) {
    const currentChannels = this.channels.sortIntBy("index");

    for (let i = size; i < currentChannels.length; i++) {
      const channel = currentChannels[i];
      channel.flagErrors({ channelDeletedButConnected: "channelDeletedButConnected" }, channel.connections.size > 0);

      if (channel.connections.size == 0) {
        this.channels.remove(channel);
      } else {
        channel.name = `- (${itx("Device.deleted", "gelöscht")})`;
        channel.raiseOnChange("errors");
      }
    }

    // remembers the channel(s) reserved in this call, so their slot size can be derived from
    // newChannelType below (they are not connected to their consumer yet at this point)
    const newlyCreatedChannels = new Set<Channel>();
    for (let i = currentChannels.length; i < size; i++) {
      const newChannel = this.createNewRequestChannel(undefined, type);
      newlyCreatedChannels.add(newChannel);
      this.channels.raiseOnChange();
    }

    const currentChannels2 = this.channels.sortIntBy("index");
    let newChannelTag = "";
    let newChannelName = "";
    // slot: running number of occupied actor-channel slots; most channelTypes occupy 1, but e.g. Lan2 occupies 2
    let slot = 0;
    for (let i = 0; i < size; i++) {
      const channel = currentChannels2[i];

      // an actor's channelType (e.g. Lan) never becomes Lan2; the slot size is instead derived from
      // the connected consumer's channelType, or from newChannelType for a not-yet-connected new channel
      let slotChannelType = channel.channelType;
      if (this.channelType == ChannelType.Lan) {
        const connectedConsumer = channel.getConnectedConsumer();
        if (connectedConsumer) {
          slotChannelType = connectedConsumer.channelType;
        } else if (newlyCreatedChannels.has(channel) && newChannelType != undefined) {
          slotChannelType = newChannelType;
        }
      }
      const channelSlotSize = channelTypeInfo(slotChannelType).channelSlotSize || 1;

      if (channelSlotSize > 1) {
        const slotNumbers: string[] = [];
        for (let s = 1; s <= channelSlotSize; s++) {
          slotNumbers.push((slot + s).toString());
        }
        newChannelTag = slotNumbers.join(",");
      } else {
        newChannelTag = (slot + 1).toString();
        // LAN actor channels are shown as plain numbers (1, 2, ...) without a leading zero
        if (newChannelTag.length == 1 && this.channelType != ChannelType.Lan) {
          newChannelTag = "0" + newChannelTag;
        }
      }
      slot += channelSlotSize;

      // a LAN actor's channels are numbered (matching the tag, incl. paired "1,2" for double-slot channels)
      // instead of the usual letter-based naming (a, b, c, ...)
      newChannelName = this.channelType == ChannelType.Lan ? newChannelTag : this.getChannelName(i);

      channel.isRequestChannel = true;

      channel.name = newChannelName;
      channel.tag = newChannelTag;
      delete channel.errors;

      if (channel.channelType != this.channelType) {
        channel.signals.clear();
      }
      if (this.channelType != ChannelType.Unknown) {
        channel.channelType = this.channelType;
      }
    }
  }

  applySceneConnectionsToKnxParameters() {
    if (this.template) {
      this.channels.forEach((channel) => {
        const knxChannel = channel.mappedChannel;
        if (
          knxChannel &&
          knxChannel.channelType != ChannelType.General &&
          knxChannel.channelType != ChannelType.Unknown
        ) {
          knxChannel.applySceneConnectionsToKnxParameters(this.knxInstanceData);
        }
      });
    }
  }

  static mergeSubChannelsToMainChannelByHierachy(channels: Array<Channel>) {
    const resultChannels = new Array<Channel>();

    channels.forEach((channel) => {
      // resultChannels.push(channel);

      const flatSubChannelArray = channel.mergeSubChannelsToMe();
      flatSubChannelArray.forEach((subChannel) => resultChannels.push(subChannel));
    });

    return resultChannels;
  }

  static mergeSubChannelsToMainChannelByIndent(channels: Array<Channel>): Array<Channel> {
    const res = new Array<Channel>();

    const channelsToProcess = [...channels];

    let mainChannel = channelsToProcess.shift();
    while (mainChannel) {
      res.push(mainChannel);

      let subChannel = channelsToProcess.shift();
      while (subChannel && subChannel.subChannelIndent > mainChannel.subChannelIndent) {
        mainChannel.mergeChannelDataToMe(subChannel);
        subChannel = channelsToProcess.shift();
      }
      mainChannel = subChannel;
    }

    return res;
  }

  async exportToKnx(includeDefaultValues: boolean): Promise<any | undefined> {
    try {
      if (this.reEvalKnxMetadataNeeded) {
        await this.updateAllFromKnxMetadataImmediately();
      }

      const channels = this.channels;
      const knxSignals: any = [];
      const exportedSignalPairs = new Set<string>();

      const addKnxSignal = (signal: Signal, address?: string) => {
        if (!address) {
          return;
        }

        const signalNumber = `${signal.number}`;
        const key = `${signalNumber}_${address}`;
        if (exportedSignalPairs.has(key)) {
          return;
        }

        exportedSignalPairs.add(key);
        knxSignals.push({
          number: signalNumber,
          address: address,
        });
      };

      channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          signal.groupAddresses.forEach((groupAdress) => {
            const address = groupAdress?.fullAddress;
            if (address && address != "") {
              addKnxSignal(signal, address);
            }
          });
          addKnxSignal(signal, signal.type.globalGroupAdress);
        });
      });

      let exportEtsInstanceData: any = {};

      const template = this.template;
      const getPNumber = (name) => {
        const firstPart = (name || "").split("_")[0];
        const number = parseInt(firstPart.split("-")[1], 10);
        return Number.isNaN(number) ? Number.MAX_SAFE_INTEGER : number;
      };
      if (template && template.knxProductData) {
        const productInfo = template.knxProductData.productInfo;
        exportEtsInstanceData = {
          item_id: productInfo.CatalogId,
          manufacturer_id: productInfo.ManufacturerId,
          OrderNumber: productInfo.OrderNumber,
          ApplicationProgramVersion: productInfo.ApplicationVersion,
          MediumType: productInfo.MediumType,
          MaskVersion: productInfo.MaskVersion,
          params: [], // this.knxInstanceData.params || {},
          modules: [], // this.knxInstanceData.modules || {},
        };

        // convert params
        const pesistentParams = this.knxInstanceData.getParams(
          includeDefaultValues ? ValueLevels.default : ValueLevels.estadaPreset,
        );
        for (const i in pesistentParams) {
          const s = pesistentParams[i];
          const ss = s.split("~"); // split value~level
          const value = ss[0];

          exportEtsInstanceData.params.push({
            name: i,
            value: value,
          });
        }
        exportEtsInstanceData.params.sort((a, b) => getPNumber(a.name) - getPNumber(b.name));

        // convert modules
        const modules = this.knxInstanceData.getModules(ValueLevels.estadaPreset);
        for (const i in modules) {
          const module = modules[i];
          const moduleData: any = [];

          for (const j in module) {
            const s = module[j];
            const ss = s.split("~"); // split value~level
            const value = ss[0];

            moduleData.push({
              name: j,
              value: value,
            });
          }

          exportEtsInstanceData.modules.push({
            name: i,
            value: { params: moduleData },
          });
          exportEtsInstanceData.modules.sort((a, b) => getPNumber(a.name) - getPNumber(b.name));
        }
      } else {
        return undefined;
      }

      let longName = this.fullTag;
      if (this.name != "") {
        longName += " - " + this.name;
      }

      const applicationProgram = this.getApplicationProgram();
      const additionalAddressCount = applicationProgram?.a?.AdditionalAddressesCount || "0";

      return {
        knxNumber: this.physicalAddress.toString(),
        etsId: this.etsId,
        area: this.areaAddress,
        line: this.lineAddress,
        tag: this.tag,
        name: longName,
        id: this.id,
        signals: knxSignals,
        instanceData: exportEtsInstanceData,
        additionalAddressCount: additionalAddressCount,
      };
    } catch (ex) {
      logger.error(ex);
    }
  }

  findFreePhysicalAddress() {
    const allAddreses = new Set<number>();
    const actors = sortArrayByProperty(state.allActors(), "physicalAddress");
    const startAdrButtons = (Math.floor(actors.length / 10) + 2) * 10 + 1;

    let adr = 2;
    actors.forEach((actor) => {
      if (!channelTypeInfo(actor.channelType).isSupply) {
        if (actor.lineAddress == this.lineAddress && actor.areaAddress == this.areaAddress) {
          allAddreses.add(parseInt(actor.physicalAddress));
        }
      }
    });

    if (!this.isActor) {
      adr = startAdrButtons;
    }

    state.allButtons().forEach((button) => {
      if ((button as Device) != this) {
        if (button.lineAddress == this.lineAddress && button.areaAddress == this.areaAddress) {
          allAddreses.add(parseInt((button as Device).physicalAddress));
        }
      }
    });

    while (allAddreses.has(adr) && adr <= 255) {
      adr++;
    }

    if (adr <= 255 && adr > 0) {
      this.physicalAddress = `${adr}`;
      // update tree
      this.area?.raiseOnChange("childCollection");
    } else {
      //
      // no free physicalAddress found in this line
      //
      this.physicalAddress = "1";
    }
  }

  getAllTagsFromSameTypeWithoutOwn(): string[] | undefined {
    const res = new Array<string>();
    let deviceArray: Array<Device> | undefined;
    if (this.className == "Button" && this.parent) {
      if (state.projectMetadata.uniqueTagsForProject) {
        deviceArray = state.allButtons();
      } else {
        deviceArray = (this.parent as Room).buttons.asArray();
      }
    } else if (this.className == "Actor" && this.parent) {
      deviceArray = (this.parent as Cabinet).actors.asArray();
    }

    deviceArray?.forEach((dev) => {
      if (this != dev) {
        if (dev.isButton) {
          if (dev.deviceType == this.deviceType) {
            res.push(dev.tag);
          }
        } else {
          res.push(dev.tag);
        }
      }
    });
    if (res.length > 0) {
      return res;
    }
    return undefined;
  }
  getAllPhysicalAddressWithoutOwnInLineAndArea(): string[] | undefined {
    const res = new Array<string>();
    const listDevices = state.allDevices();
    const lastDotIndexDevice = this.fullPhysicalAddress.lastIndexOf(".");
    const lineandAreaAdresses = this.fullPhysicalAddress.substring(0, lastDotIndexDevice);
    listDevices.forEach((dev) => {
      if (dev != this) {
        const lastDotIndex = dev.fullPhysicalAddress.lastIndexOf(".");
        if (lineandAreaAdresses == dev.fullPhysicalAddress.substring(0, lastDotIndex)) {
          res.push(dev.physicalAddress);
        }
      }
    });
    if (res.length > 0) {
      return res;
    }
    return undefined;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.channels.forEach((channel) => {
      res.push(channel);
    });
    //?? or not ?
    this.knxChannels.forEach((channel) => {
      res.push(channel);
    });

    /// mwe 7.3.24:
    /// würde eigentlich nur gebracuht wenn man in ein anderes project kopiert
    ///
    if (this.template) {
      res.push(this.template);
    }

    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();

      this.channels.mapAfterCopy();
      this.knxChannels.mapAfterCopy();
      if (state.targetObjectRegistry.getObject(this._sourceId)) {
        //
        // dann findet "copy & paste" im gleichen Projekt statt......
        // und wir packen "this.templateId" nicht an,
        // da in der Kopie wir ja richtigerweise einfach weiter auf das gleiche Template verweisen
        //
        console.log(`paste templateId: ${this.templateId} ${this.template?.name}`);
      } else {
        this.template?.mapAfterCopy();
        this.templateId = this.template?.id || "";
      }
    }
  }
  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj, page } = params;
    const undoObjects: Array<BaseModel> = [this];

    // physical Adress should be unique, so we search a free one for the copy
    this.findFreePhysicalAddress();

    const sourceTemplate = this.template?.sourceObject as DeviceTemplate;
    this.parent = parent;
    const siblings = parent.className == "Room" ? (parent as Room).consumers : (parent as Cabinet).actors;
    if (isCopyRootObj) {
      this.updateNameInPaste(siblings);
    }
    if (isCopyRootObj || state.projectMetadata.uniqueTagsForProject) {
      this.updateTagInPaste(siblings);
    }

    if (sourceTemplate) {
      // if original template is in this Project -> use orginal template here
      this.templateId = sourceTemplate.id;
    } else {
      // original template isn`t in this Project -> attach copied template to project
      if (this.template) {
        const templateGroupName = this.className;
        const templateGroup = getTemplateGroup(templateGroupName);
        if (templateGroup) {
          let found: DeviceTemplate | undefined;
          templateGroup.templates.forEach((template) => {
            if (template.knxProductData.modelDataId == this.template?.knxProductData.modelDataId) {
              found = template;
            }
          });
          if (found) {
            this.template = found;
          } else {
            this.template.parent = templateGroup;
            templateGroup.templates.add(this.template);
            undoObjects.concat(
              await this.template.executePaste({
                parent: templateGroup,
                isCopyRootObj: false,
              }),
            );
          }
          this.updateAllFromKnxMetadata();
        }
      }
    }

    //
    // connections
    //

    this.channels.forEach((channel) => {
      channel.connections.forEach((connection) => {
        if (page) {
          connection.executePasteHelper({
            isCopyRootObj: isCopyRootObj,
            initiator: channel,
            undoObjects: undoObjects,
            page: page,
          });
        } else {
          connection.delete();
        }
      });
    });
    // has to be set becaus tehe automatic deleing from channels without connections
    this.isDeleted = false;

    later(() => {
      this.channels.forEach((channel) => {
        channel.connections.forEach((connection) => {
          connection.updateEndPoints();
        });
      });
    });

    return undoObjects;
  }

  createNewKnxChannel(data: any, parent: BaseModel): Channel {
    if (this.isActor) {
      return new Channel({ ...data, isRequestChannel: false }, parent);
    } else {
      return new ButtonChannel({ ...data, isRequestChannel: false }, parent);
    }
  }

  createNewRequestChannel(isDali?: boolean, type?: "Button" | "Actor"): Channel {
    if (this.isButton) {
      const channel = new ButtonChannel({ tag: "??", name: "??", isRequestChannel: true }, this);
      this.channels.addWithoutUpdate(channel);
      return channel;
    }

    if (this.channels.size >= this.maxChannelSize && !channelTypeInfo(this.channelType).isSupply && !this.template) {
      let nextDevice = new Device();
      const cabinet = this.parent as Cabinet;
      let nextActor = cabinet.findActorWithSameTypeWithoutTemplateAndNotmaxSize(this.channelType, isDali);
      if (!nextActor) {
        nextActor = cabinet.autoCreateActor(this.channelType, isDali);
      }
      if (nextActor) {
        nextDevice = nextActor;
      } else {
        logger.log("Problems with autoconnect new Actor, wrong channel type?");
      }

      const newChannel = nextDevice.createNewRequestChannel();
      return newChannel;
    }

    const CHANNEL_NAMES = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

    let sIndex = `${this.channels.size + 1}`;

    let channel: Channel;

    let newChannelName = "";

    for (let i = 1; i < 50; i++) {
      let index = `${i}`;
      if (index.length == 1) {
        index = `0` + index;
      }
      const usedChannel = this.channels.getByPropertyValue("tag", index);
      if (!usedChannel) {
        sIndex = index;
        newChannelName = CHANNEL_NAMES.substring(i - 1, i);
        break;
      }
    }

    channel = new Channel({ tag: sIndex, name: newChannelName, isRequestChannel: true }, this);

    channel.channelType = this.channelType;

    this.channels.addWithoutUpdate(channel);

    return channel;
  }

  checkIfRequestChannelsAreCompatibleToKnxChannels() {
    this.channels.forEach((channel) => {
      if (channel.knxChannel) {
        const err =
          channel.channelType != ChannelType.Disabled &&
          !areChannelTypesCompatible(channel.channelType, channel.knxChannel.channelType, this);
        channel.flagErrors({ channelTypeError: "" }, err);
      }
    });
  }

  cleanupDuplicateUnusedRequestChannels() {
    this.channels.sortBy("tag").forEach((channel) => {
      if (!channel.knxChannel && channel.connections.size == 0 && !channel.hasConnectedSignals) {
        const isDuplicateChannel = this.channels.findFirst((c) => c !== channel && c.tag == channel.tag);

        if (isDuplicateChannel) {
          channel.delete();
          this.channels.removeWithoutUpdate(channel);
        }
      }
    });
  }

  updateRequestChannelsFromKnxChannels() {
    if (this.template?.genericParams) return;
    if (this.template) {
      //
      // Remove all requirementChannels
      //    without a matching KnxChannel AND
      //    without a connection
      //

      const importedChannelTag = "@I";
      this.channels.sortBy("tag").forEach((channel) => {
        if (
          !channel.mappedChannel &&
          channel.connections.size == 0 &&
          !channel.hasConnectedSignals &&
          !channel.tag.startsWith(importedChannelTag)
        ) {
          if (this.isButton) {
            if (channel.channelType == ChannelType.TemperatureMeasurement) {
              channel.delete();
            } else if (!channel.existsBeforeThisEvaluation) {
              channel.delete();
              this.channels.removeWithoutUpdate(channel);
            }
          } else {
            if (!this.template?.knxProductData.hasScript_mapChannelToTag) {
              //
              // MWE 26.6.2024
              //
              // do not any longer auto delete ButtonChannels
              // this is massivly disturbing scripting for auto configuration
              //
              channel.delete();
              this.channels.removeWithoutUpdate(channel);
            }
          }
        }
      });

      //
      // for actors: copy channelType from knxChannel to requirmentChannel
      // for buttons: channelType of requirmentChannel is allways "leading"
      //

      this.channels.sortBy("tag").forEach((channel) => {
        if (this.isActor || channel.isActorChannel) {
          if (channel.knxChannel) {
            channel.channelType = channel.knxChannel.channelType;
          }
        } else {
          //
          // MWE 22.7.2026
          //
          // ButtonChannels die NICHT verbunden sind,
          // UND nicht vom Benutzer explizit auf einen channelType gesetzt wurden,
          //
          // => bekommen den channelType vom knxChannel
          //
          if (
            channel.knxChannel &&
            channel.connections.size == 0 &&
            channel.isButtonChannel &&
            !(channel as ButtonChannel).channelTypeIsSetByUser
          ) {
            channel.channelType = channel.knxChannel.channelType;
          }
        }
      });

      //
      // create for all knxChannels WITHOUT a mapping requirementChannel a requirement channel
      //

      if (!this.isButton) {
        this.knxChannels.sortBy("tag").forEach((knxChannel) => {
          if (knxChannel.channelType != ChannelType.Unknown && knxChannel.channelType != ChannelType.General) {
            let requestChannel = knxChannel.mappedChannel;

            if (!requestChannel) {
              requestChannel = this.createNewRequestChannel();
              requestChannel.mappedChannel = knxChannel;
              requestChannel.channelType = knxChannel.channelType;
            }
            requestChannel.isRequestChannel = true;
          }
        });

        this.knxChannels.forEach((knxChannel) => {
          const requestChannel: ButtonChannel = knxChannel.requestChannel as ButtonChannel;

          if (knxChannel.channelType != ChannelType.General && knxChannel.channelType != ChannelType.Unknown) {
            if (!requestChannel) {
              logger.error("requirementChannel missing");
            } else {
              requestChannel.tag = knxChannel.tag;
            }
          }
        });

        ///////////////////////////////////////////////////
        //
        // all channels without knxChannels get Tags beginning with "80"
        //
        const channelsWithoutKnxChannel = this.channels.sortBy("tag").filter((channel) => !channel.mappedChannel);

        let i = 80;
        channelsWithoutKnxChannel.forEach((channel) => {
          //
          // for imported channel, keep tag
          //
          if (!channel.tag.startsWith("@I")) {
            channel.tag = `${i}`;
          }

          i++;
        });
      }
    }
  }

  /**
   * Übernimmt abweichende KNX-Kanal-Tags nach einem Template-Wechsel eines Sensors.
   *
   * Der bisherige Sensor besitzt bereits RequestChannels, an denen Namen,
   * Einstellungen und Consumer-Verbindungen hängen. Das neue Produkt kann für
   * dieselben Funktionen andere Tags verwenden. Deshalb werden die vorhandenen
   * RequestChannels weiterverwendet, statt sie durch neue Objekte zu ersetzen.
   *
   * Ablauf:
   * 1. Beide Kanalbestände mit sortBy("tag") sortieren und als lokale Arbeitsarrays
   *    übernehmen. Gelöschte Objekte und Nicht-RequestChannels werden ausgeschlossen.
   *    Bei KNX-Kanälen nur Tags mit führendem T berücksichtigen; ein X an zweiter
   *    Stelle (TX...) schließt insbesondere die Sonderkanäle von dieser Zuordnung aus.
   * 2. Jeden RequestChannel auf einen KNX-Kanal mit identischem Tag prüfen. Solche
   *    Paare aus beiden Arbeitsarrays entfernen; ihre Tags und Zuordnungen bleiben
   *    unverändert. Entfernt wird nur aus den Arrays, niemals aus dem Device.
   * 3. Die übrigen Kanäle in ihrer sortierten Reihenfolge paaren: erster Request-
   *    mit erstem KNX-Kanal, zweiter mit zweitem usw. Die kürzere Liste begrenzt
   *    die Anzahl; überzählige Kanäle werden weder gelöscht noch umbenannt.
   * 4. Alte Zuordnungen dieser Paare zunächst auf beiden Seiten lösen. Erst danach
   *    die neuen Paare setzen, damit ein späteres Lösen keine bereits neu gesetzte
   *    Gegenrichtung zerstört, beispielsweise bei vertauschten Zuordnungen.
   * 5. Der RequestChannel erhält den Tag seines KNX-Kanals und wird über den
   *    vorhandenen Mapping-Setter beidseitig mit ihm verbunden. Seine Identität,
   *    sein Name, sein ChannelType und seine Consumer-Verbindungen bleiben erhalten.
   *
   * Beispiel: Request [T01, T02, TMP], KNX [TA, TB, TMP, TX_T]. TMP bleibt
   * bestehen; TX_T wird ignoriert; T01 wird TA, T02 wird TB. Die Reihenfolge ist der
   * ausdrücklich verwendete Fallback; eine fachliche Erkennung findet nicht statt.
   *
   * Aufruf nach dem Laden/Auswerten des neuen Templates und vor dem abschließenden
   * Entfernen unbenutzter Kanäle. Nach erfolgreicher Zuordnung sind die Tags gleich;
   * ein erneuter Aufruf lässt diese Paare unverändert.
   */
  mapNonStandartSensorChannelTags(oldRequestChannels: Channel[]): void {
    const knxChannels = this.knxChannels
      .sortBy("tag")
      .filter((channel) => !channel.isDeleted && channel.tag.startsWith("T") && channel.tag[1] !== "X");

    // Bereits passende Channels (gleiche Tags) aus beiden Arbeitslisten herausnehmen.
    for (let index = 0; index < oldRequestChannels.length; index++) {
      const requestChannel = oldRequestChannels[index];
      const knxChannel = requestChannel.knxChannel;
      if (!knxChannel) continue;
      if (requestChannel.tag === knxChannel.tag) continue;

      const knxIndex = knxChannels.findIndex((channel) => channel === knxChannel);
      if (knxIndex < 0) continue;
      oldRequestChannels.splice(index, 1);
      knxChannels.splice(knxIndex, 1);
      index--;
    }

    // Erst alle betroffenen alten Zuordnungen lösen, dann neue Paare bilden.
    const count = Math.min(oldRequestChannels.length, knxChannels.length);
    for (let index = 0; index < count; index++) {
      oldRequestChannels[index].mappedChannel = undefined;
      knxChannels[index].mappedChannel = undefined;
    }

    for (let index = 0; index < count; index++) {
      const requestChannel = oldRequestChannels[index];
      const knxChannel = knxChannels[index];

      console.log(`Mapping request channel ${requestChannel.tag} to KNX channel ${knxChannel.tag}`);

      requestChannel.tag = knxChannel.tag;

      requestChannel.mappedChannel = knxChannel;
    }
  }

  async setAndLoadTemplate(templateId: string) {
    if (this.templateId !== templateId) {
      this.knxParameterViewerHasRundOnce = false;

      const oldRequestChannels = this.channels.asArray();

      const template = state.objectRegistry.getObject(templateId) as DeviceTemplate;
      await this.setTemplate(template);
      if (this.isSensor) this.mapNonStandartSensorChannelTags(oldRequestChannels);
      state.eventLogger.addEvent(EventKey.ProductSelected, { deviceId: this.id, templateId });

      //
      // cleanup:
      //   Delete all Channels without knxChannel AND without connections
      //

      this.channels.sortBy("tag").forEach((channel) => {
        if (
          !this.shouldPreserveUnusedChannel(channel) &&
          !channel.knxChannel &&
          channel.connections.size == 0 &&
          !channel.hasConnectedSignals
        ) {
          channel.delete();
        }
      });

      /////////////////////////////////////////////////////////////////////////////

      this.knxInstanceData.raiseOnChange();
      this.raiseOnChange("template");
    }
  }

  getAllConnectionsFromChannels(): Array<Connection> {
    let allConnectionsFromChannels = new Array<Connection>();
    this.channels.forEach((channel) => {
      allConnectionsFromChannels = allConnectionsFromChannels.concat(channel.connections.asArray());
    });

    return allConnectionsFromChannels;
  }

  get allChannelsAreInUse(): boolean {
    let res = true;
    this.channels.forEach((channel) => {
      if (channel.connections.size == 0) {
        res = false;
      }
    });

    return res;
  }

  allKnxSignals(): Array<Signal> {
    const res = new Array<Signal>();

    // Using knxChannels here instead of channels
    // as Config flags needed for signals such as Presence and Push button operation
    this.knxChannels.forEach((knxChannel) => {
      knxChannel.signals.forEach((signal) => res.push(signal));
    });
    return sortArrayByIntProperty(res, "number");
  }

  allSignals(): Array<Signal> {
    const res = new Array<Signal>();

    // Using knxChannels here instead of channels
    // as Config flags needed for signals such as Presence and Push button operation
    this.channels.forEach((channel) => {
      channel.signals.forEach((signal) => res.push(signal));
    });
    return sortArrayByIntProperty(res, "number");
  }

  channelsSortedForDisplay(show?: {
    buttonChannels?: boolean;
    actorChannels?: boolean;
    statusChannels?: boolean;
    logicChannels?: boolean;
    environmentChannel?: boolean;
    temperatureMeasurementChannel?: boolean;
    channelWithoutKnxChannel?: boolean;
    generalChannel?: boolean;
    specialChannel?: boolean;
    patchChannels?: boolean;
    longPressChannels?: boolean;
    manualChannel?: boolean;
    unknownChannel?: boolean;
    ignoreChannel?: boolean;
    defaultSortingAndFiltering?: boolean;
  }): Array<Channel> {
    return channelsSortedForDisplayFromDevice(this, show);
  }

  channelsSortetAndFilteredForListspdf(): Array<Channel> {
    return channelsSortedForDisplayFromDevice(this, { defaultSortingAndFiltering: true }).filter(
      (channel) => channel.channelType != ChannelType.Environment,
    );
  }

  channelsSortedForRockerMapping(): Array<Channel> {
    const childChannels = new Array<Channel>();
    const childChannelsWithoutKnxChannel = new Array<Channel>();

    // mwe todo 25.6.204 :  handle ChannelType.StatusDisplaySwitch

    let environmentChannel: Channel | undefined;
    let temperatureMeasurementChannel: Channel | undefined;

    this.channels.sortBy("tag").forEach((channel) => {
      if (channel.channelType == ChannelType.Environment && environmentChannel) {
        environmentChannel = channel;
      } else if (channel.channelType == ChannelType.TemperatureMeasurement) {
        temperatureMeasurementChannel = channel;
      } else {
        childChannels.push(channel);
      }
    });

    let res = childChannels.concat(childChannelsWithoutKnxChannel);

    if (temperatureMeasurementChannel) {
      res.push(temperatureMeasurementChannel);
    }
    if (isUiModeExpert()) {
      if (environmentChannel) {
        res.push(environmentChannel);
      }
    }

    res = filterChannelsByUserRole(this, res);

    return res;
  }

  convertDataURIToBinary(base64: string): Uint8Array {
    const raw = window.atob(base64);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);

    if (this.template) {
      if (this._channelType != ChannelType.Lock) {
        this._channelType = ChannelType.Unknown;
      }
    }

    // if (this.isButton) { // buttons können auch andere Channels haben, wie Aktorchannels!!!!!!
    //   for (const buttonChannel of this.channels.asArray()) {
    //     if (buttonChannel.className != "ButtonChannel") {
    //       this.logConsistencyError("buttonChannel is not a ButtonChannel. Fixed");
    //       const data = buttonChannel.toJSON();
    //       const fixChannel = new ButtonChannel({}, this);
    //       fixChannel.fromJSON(data, this);
    //       this.channels.removeWithoutUpdate(buttonChannel);
    //       this.channels.addWithoutUpdate(fixChannel);
    //       buttonChannel.delete();
    //     }
    //   }
    //   for (const buttonChannelKnx of this.knxChannels.asArray()) {
    //     if (buttonChannelKnx.className != "ButtonChannel") {
    //       this.logConsistencyError("buttonChannelKnx is not a ButtonChannel. Fixed");
    //       const data = buttonChannelKnx.toJSON();
    //       const fixChannel = new ButtonChannel({}, this);
    //       fixChannel.fromJSON(data, this);
    //       this.channels.removeWithoutUpdate(buttonChannelKnx);
    //       this.channels.addWithoutUpdate(fixChannel);
    //       buttonChannelKnx.delete();
    //     }
    //   }
    // }
    //
    //constistens test for product
    //
    if (this.templateId != "" && this.template == undefined) {
      this.logConsistencyError(`missing template: id=${this.templateId} `); // no itx
      this.template = undefined;
    }

    if (this.template && !this.template.genericParams) {
      if (this.template.knxProductData.modelDataId == "" || this.template.knxProductData.modelDataId == undefined) {
        this.logConsistencyError(`knxProductData.modelDataId missing `); // no itx
        this.template = undefined;
      }
    }
    // check if physicalAddress is double in the same line and area
    // devices without an application program (e.g. bus power supplies) share "-" as address and are not real duplicates
    let hasDoublePhysicalAddress = false;
    state.allDevices().forEach((dev) => {
      if (
        dev != this &&
        dev.fullPhysicalAddress == this.fullPhysicalAddress &&
        this.template &&
        this.physicalAddress != "" &&
        this.physicalAddress != "1" &&
        this.physicalAddress != "-" &&
        dev.physicalAddress != "-"
      ) {
        hasDoublePhysicalAddress = true;
      }
    });

    if (hasDoublePhysicalAddress) {
      this.logConsistencyError(`fullPhysicalAddress is double: ${this.fullPhysicalAddress} `, "doublePhysicalAdresses"); // no itx
    }

    this.channels.consistencyCheck(this);
    // knxChannels (physical side) hold their own signals/errors and were never checked before - fixes stale errors like toMuchGroupAdresses
    this.knxChannels.consistencyCheck(this);
  }

  findConnectableChannel(
    connectionType: ConnectionType,
    partnerObject: ConnectableObject,
    rule?: "hard" | "soft", // no itx
  ): Channel | undefined {
    let res: Channel | undefined;
    return res;
  }

  getFullPhysicalAdressIsDouble(): boolean {
    let foundSameAdress = false;
    if (channelTypeInfo(this.channelType).isSupply) {
      return foundSameAdress;
    }
    state.allDevices().forEach((dev2) => {
      if (
        this.fullPhysicalAddress == dev2.fullPhysicalAddress &&
        this != dev2 &&
        !channelTypeInfo(dev2.channelType).isSupply &&
        this.tag != "" &&
        this.physicalAddress != "-"
      ) {
        logger.devError(`consistencyCheck: ${this.fullTag} double full fullPhysicalAddress `);
        // here set error on
        foundSameAdress = true;
      }
    });
    return foundSameAdress;
  }

  // -----------------------------------------------------------------------------
  // programming
  // -----------------------------------------------------------------------------
  _programming: DeviceProgramming | undefined;

  get programming(): DeviceProgramming {
    if (!this._programming) {
      this._programming = new DeviceProgramming(this);
    }
    return this._programming;
  }

  // -----------------------------------------------------------------------------

  get rules(): KnxRules | undefined {
    return this.template?.knxProductData.currentRules;
  }

  moveManualSignals() {
    const manualRequestSignalChannel = this.channels.findFirst((c) => c.tag == TAG_MANUAL_SIGNALS);
    if (!manualRequestSignalChannel || manualRequestSignalChannel.signals.size === 0) return;

    // Create the ManualSignals KNX channel and link it to the request channel.
    // This must happen unconditionally – even when all signals were properly matched to regular
    // channels and no TAG_SPECIAL / TAG_IGNORE channel exists (empty SPECIAL channels are
    // removed by removeEmptyKnxChannels before this method is called).
    const newManualKnxChannel = this.createNewKnxChannel({ tag: TAG_MANUAL_SIGNALS }, this);
    this.knxChannels.addWithoutUpdate(newManualKnxChannel);
    newManualKnxChannel.channelType = ChannelType.ManualSignals;
    newManualKnxChannel.name = itx("Channel.ManualSignals", "Manuelle Signalverbindungen");
    newManualKnxChannel.requestChannel = manualRequestSignalChannel;
    manualRequestSignalChannel.knxChannel = newManualKnxChannel;

    // Preferred source channels for backwards compatibility: TAG_IGNORE (old saved projects)
    // and TAG_SPECIAL (newly rebuilt projects with unmatched signals).
    // If the signal was fully matched to a regular channel during rebuild it will not be in
    // IGNORE/SPECIAL, so fall back to searching all KNX channels.
    const preferredSourceChannels = [
      this.knxChannels.findFirst((c) => c.tag == TAG_IGNORE),
      this.knxChannels.findFirst((c) => c.tag == TAG_SPECIAL),
    ].filter((c): c is Channel => c != null);

    for (const requestSignal of manualRequestSignalChannel.signals) {
      let knxSignal: Signal | undefined;
      let sourceKnxChannel: Channel | undefined;

      // Search preferred channels first
      for (const ch of preferredSourceChannels) {
        knxSignal = ch.signals.findFirst((s) => s.number == requestSignal.number);
        if (knxSignal) {
          sourceKnxChannel = ch;
          break;
        }
      }

      // Fall back: signal may have been placed in a regular channel during rebuild
      if (!knxSignal) {
        for (const ch of this.knxChannels.asArray()) {
          if (ch === newManualKnxChannel) continue;
          knxSignal = ch.signals.findFirst((s) => s.number == requestSignal.number);
          if (knxSignal) {
            sourceKnxChannel = ch;
            break;
          }
        }
      }

      if (knxSignal && sourceKnxChannel) {
        newManualKnxChannel.signals.addWithoutUpdate(knxSignal);
        knxSignal.parent = newManualKnxChannel; // update KNX signal's parent to the new channel
        sourceKnxChannel.signals.removeWithoutUpdate(knxSignal);
      }
    }
  }

  moveImportedSignalsToChannels() {
    function matchSignals(sImport: Signal, knxChannelSignal: Signal): boolean {
      if (sImport.number?.startsWith("2-")) {
        //
        // handle complex signal numbering with refId and channelId
        //
        const sImport_RefIds = new KnxIds(sImport.knxRefId);

        const knxChannelSignal_RefIds = new KnxIds(knxChannelSignal.knxRefId);
        const knxChannelSignal_ChannelIds = new KnxIds(knxChannelSignal.knxChannelId);

        console.log("module signal found", sImport.number, sImport);

        const c1 = knxChannelSignal_ChannelIds.getId("M");
        const c2 = sImport_RefIds.getId("M");

        const o1 = knxChannelSignal_RefIds.getId("O");
        const o2 = sImport_RefIds.getId("O");

        //
        // Match ModuleId and (Com-) ObjectId
        //

        return c1 == c2 && o1 == o2;
      } else {
        return sImport.number == knxChannelSignal.number;
      }
    }

    try {
      let importSignals = new Array<Signal>();

      const channelForSignalsToMove = this.channels.findAsArray((c) => !c.knxChannel && c.tag.startsWith("@I"));

      if (channelForSignalsToMove.length == 0) {
        return;
      }

      for (const importChannel of channelForSignalsToMove) {
        importSignals = importSignals.concat(importChannel.signals.asArray());
      }

      let debugIndex = 0;
      importSignals.forEach((s) => {
        (s as any).debugIndex = debugIndex++;
        console.log("import signal:", (s as any).debugIndex, s.number, s.knxRefId, s.knxChannelId);
      });

      //
      // create requestChannels for all knxChannels without requestChannel
      // which have signals in the importSignals
      //

      this.knxChannels.forEach((knxChannel) => {
        let requestChannel = knxChannel.requestChannel;
        if (!requestChannel) {
          requestChannel = this.createNewRequestChannel();
          requestChannel.knxChannel = knxChannel;
          requestChannel.channelType = knxChannel.channelType;
          requestChannel.tag = requestChannel.tag;
          requestChannel.name = knxChannel.name;
        }

        let oneOrMoreSignalsMoved = false;

        for (const knxChannelSignal of knxChannel.signals.asArray()) {
          console.log(
            "import signal:",
            (knxChannelSignal as any).debugIndex,
            knxChannelSignal.number,
            knxChannelSignal.knxRefId,
            knxChannelSignal.knxChannelId,
          );

          const matchedImportSignal = importSignals.find((s) => matchSignals(s, knxChannelSignal));
          if (matchedImportSignal) {
            //
            // move signal from import channel to current channel
            //

            //
            // here we match via number
            //  * request Signals dont has kxRefId etc. !!!!
            //  * knxSignal.number is here calulated correctly in knxParameterViewer
            //
            const existingRequestSignal = requestChannel.signals.findFirst(
              (requestSignal) => requestSignal.number == knxChannelSignal.number,
            );
            if (existingRequestSignal) {
              //
              // already a signal with this number in the request channel
              // merge group addresses to the existing signal
              //

              matchedImportSignal.groupAddresses.forEach((ga) => {
                const existingGA = existingRequestSignal.groupAddresses
                  .asArray()
                  .find((g) => g.fullAddress == ga.fullAddress);
                if (!existingGA) {
                  ga.removeSignalBackpointer(matchedImportSignal);
                  existingRequestSignal.groupAddresses.add(ga);
                  ga.addSignalBackpointer(existingRequestSignal);
                }
              });

              matchedImportSignal.groupAddresses.clear();
              matchedImportSignal.delete();

              oneOrMoreSignalsMoved = true;
            } else {
              //
              // no existing requestSingal for matched knxSiganl
              // move imported signal to requestChannel
              //
              (matchedImportSignal.parent as Channel)?.signals.remove(matchedImportSignal);
              requestChannel.signals.add(matchedImportSignal);

              matchedImportSignal.number = knxChannelSignal.number; // für die fälle mit 2-... nummerierung im ets-import
              matchedImportSignal.parent = requestChannel;
            }
          }
        }

        if (oneOrMoreSignalsMoved) {
          //
          // remove empty import channel(s)
          //
          for (const channel of channelForSignalsToMove) {
            if (channel.signals.size == 0) {
              channel.delete();
            }
          }
        }
      });
    } catch (ex) {
      logger.error(ex);
    }
  }

  handleSceneDataSignals(newSignals: Signal[]): Signal[] {
    let resSignals: Signal[] = [];

    const filteredSignals = newSignals.filter((s) => {
      return !s.isChannelIndependent;
    });

    let lastAssignedSignal: Signal | undefined = undefined;
    let firstAssignmentIsToPrev: boolean | undefined;

    for (let i = 0; i < filteredSignals.length; i++) {
      const signal = filteredSignals[i];
      const prevSignal = i > 0 ? filteredSignals[i - 1] : undefined;
      const prevPrevSignal = i > 1 ? filteredSignals[i - 2] : undefined;
      const nextSignal = i < filteredSignals.length - 1 ? filteredSignals[i + 1] : undefined;

      let doAssignToPrev: boolean | undefined;

      if (signal.name == noItx("@DUMMY-SceneData@")) {
        if (prevSignal && nextSignal) {
          const prevNameShort = nameToShortName(prevSignal.channelName);
          const prevPrevNameShort = prevPrevSignal ? nameToShortName(prevPrevSignal.channelName) : undefined;
          const nextNameShort = nameToShortName(nextSignal.channelName);
          if (prevNameShort == nextNameShort) {
            doAssignToPrev = true;
          } else {
            if (prevPrevNameShort == prevNameShort && prevSignal?.type.name == SignalTypes.Scene.name) {
              doAssignToPrev = true;
            }
          }
        }

        if (i == 0) {
          doAssignToPrev = false;
          firstAssignmentIsToPrev = false;
        }

        if (
          doAssignToPrev == undefined &&
          firstAssignmentIsToPrev !== undefined &&
          (signal.rawSceneData?.length || 0) == 1
        ) {
          doAssignToPrev = firstAssignmentIsToPrev;
        }

        if (doAssignToPrev == undefined) {
          //
          //
          //
          console.log(
            "Cannot determine to which signal to assign the rawSceneData, checking group addresses:",
            signal.number,
            signal.name,
          );

          if (
            lastAssignedSignal &&
            (lastAssignedSignal.rawSceneData?.length || 0) == 1 &&
            (signal.rawSceneData?.length || 0) > 1
          ) {
            //
            // zuletzt wurde wohl nur der ScenesEnabling Parameter zugewiesen
            // und jetzt haben wir mehr als einen eintrag.
            // das müsssen somit die Detail-Daten sein für Scenennummer und ScenenProperties
            //

            //
            // wir attachen diese Daten an das zuletzt zugewiesene Signal
            //

            lastAssignedSignal.rawSceneData = [
              ...(lastAssignedSignal.rawSceneData || []),
              ...(signal.rawSceneData || []),
            ];

            //
            // diesen Vorgang führen wir nur einmal durch
            //

            lastAssignedSignal = undefined;

            continue;
          }

          doAssignToPrev = false;
        }

        if (doAssignToPrev == true && prevSignal) {
          ((signal.name = itx("Device.assignedtoprevious", " assigned to previous signal:")),
            prevSignal.number,
            prevSignal.name);
          resSignals.push(signal);

          lastAssignedSignal = prevSignal;
          prevSignal.rawSceneData = [...(prevSignal.rawSceneData || []), ...(signal.rawSceneData || [])];

          console.log(
            "Merged rawSceneData into signal:",
            prevSignal.number,
            prevSignal.name,
            JSON.stringify(prevSignal.rawSceneData),
          );

          firstAssignmentIsToPrev = true;
        } else if (doAssignToPrev == false && nextSignal) {
          ((signal.name = itx("Device.assignedtonext", " assigned to next signal:")),
            nextSignal.number,
            nextSignal.name);
          resSignals.push(signal);

          lastAssignedSignal = nextSignal;

          nextSignal.rawSceneData = [...(nextSignal.rawSceneData || []), ...(signal.rawSceneData || [])];

          firstAssignmentIsToPrev = false;
        } else {
          //
          // keep it for debugging
          //
          console.log("keep rawSceneData signal:", signal.number, signal.name);
          resSignals.push(signal);
        }
      } else {
        resSignals.push(signal);
      }
    }

    //
    // MDT DALI Gateway hat eine ScenenSignal im ChannelIndependent Bereich, NICHT pro "Kanal"
    //
    // Auch sind Datum Uhrzeit etc. meist in diesem Bereich.
    // Somit fügen wir alle Signale aus diesem Bereich hier wirder hinzu,
    // und erhalten somit die Origanl Menge an Signalen,
    // abzuglich der "DUMMY-SceneData" Signale die ja nur dem Transport der ScenenInfomation gedient haben.
    //

    resSignals = resSignals.concat(newSignals.filter((s) => s.isChannelIndependent));

    return resSignals;
  }

  createEnvironmentChannel(): Channel {
    const data = {
      tag: TAG_ENVIRONMENT,
      name: "Environment",
      channelType: ChannelType.Environment,
      isRequestChannel: false,
    };
    const environmentChannel = this.isActor ? new Channel(data, this) : new ButtonChannel(data, this);
    return environmentChannel;
  }

  daliUpgrade() {
    function updateConsumerConnectionsForDali(device: Device, isDali: boolean) {
      // check first if we have a other save version ( hast to be <2 ) so that only its executet once
      if (state.projectMetadata.version < 4 && isDali) {
        device.channels.forEach((channel) => {
          const connectedConsumer = channel.getConnectedConsumer();
          if (connectedConsumer) {
            connectedConsumer.isDali = true;
            // update connection
            const connection = channel.getConnectionWith(connectedConsumer);
            if (connection) {
              connection.checkForbidden();
              connection.raiseOnChange("isDali");
            }
          }
        });
      }
    }
    if (this.template && (this.template?.knxProductData.productInfo.IsDali == "true") != this._isDali) {
      this._isDali = this.template?.knxProductData.productInfo.IsDali == "true";
      this.channels.forEach((channel) => {
        channel.connections.forEach((connection) => {
          connection.checkForbidden();
          connection.raiseOnChange("isDali");
        });
      });

      updateConsumerConnectionsForDali(this, this.template?.knxProductData.productInfo.IsDali == "true");
    }
  }

  localConsistencyCheck() {
    this.daliUpgrade();

    if (!isUiModeDeveloper()) {
      return;
    }
    state.consistencyCheckLog = [];
    this.consistencyCheck(this.parent as BaseModel);
    if (state.consistencyCheckLog.length > 0) {
      showConsitencyErrorsDialog(state.consistencyCheckLog);
    }
  }

  ///////////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // panelConfig
  // -----------------------------------------------------------------------------
  _panelConfig: PanelConfigs | undefined | null = undefined;
  get panelConfig(): PanelConfigs | undefined | null {
    if (this._panelConfig == undefined || isUiModeProductAdmin()) {
      this.loadPanelConfig();
    }
    return this._panelConfig;
  }

  set panelConfig(value: PanelConfigs | undefined | null) {
    this._panelConfig = value;
  }

  //////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // mapChannelTypeConfig
  // -----------------------------------------------------------------------------
  _mapChannelTypeConfig: MapChannelTypeConfig | undefined | null = undefined;
  get mapChannelTypeConfig(): MapChannelTypeConfig | undefined | null {
    if (this._mapChannelTypeConfig == undefined || isUiModeProductAdmin()) {
      this.loadMapChannelTypeConfig();
    }
    return this._mapChannelTypeConfig;
  }

  set mapChannelTypeConfig(value: MapChannelTypeConfig | undefined | null) {
    this._mapChannelTypeConfig = value;
  }

  // -----------------------------------------------------------------------------

  loadMapChannelTypeConfig() {
    const result = this.template?.knxProductData.currentRules?.mapChannelType(this);
    if (result) {
      this.mapChannelTypeConfig = result;
    } else {
      this.mapChannelTypeConfig = null;
    }
  }

  loadPanelConfig() {
    const result = this.template?.knxProductData.currentRules?.panelConfig(this);

    if (result) {
      this.panelConfig = result;
    } else {
      this.panelConfig = null;
    }
  }

  substitutePropertyPlaceholders(input: string, instanceId: string | undefined): string {
    // get propeertyId inside text:
    //   text0 = `<%${TextParameterRefId}%>`;

    let cnt = 0;
    let startIndex = -1;
    do {
      startIndex = input.indexOf("<%");
      if (startIndex != -1) {
        const endIndex = input.indexOf("%>", startIndex);
        if (endIndex != -1) {
          const placeholder = input.substring(startIndex, endIndex + 2);
          const propertyId = placeholder.substring(2, placeholder.length - 2);
          const v = this.knxInstanceData.getValue(propertyId, instanceId, true) || "";
          input = input.replace(placeholder, v.toString());
        }
      }
    } while (startIndex >= 0 && cnt++ < 3);

    return input;
  }
}

function modifyChannelTypesForBinaryInput_Lock(newChannels: Channel[]) {
  newChannels.forEach((channel) => {
    if (channel.channelType == ChannelType.Switch) {
      channel.channelType = ChannelType.Lock;
      channel.signals.forEach((signal) => {
        if (signal.type.name == SignalTypes.Switch.name) {
          signal.type = SignalTypes.Lock;
        }
      });
    }
  });
}

function determineChannelTypeSensitiveSignalTypes(newChannels: Channel[], isActor: boolean) {
  newChannels.forEach((channel) => {
    channel.signals.forEach((signal) => {
      if (signal.determinedFromId == "manual" || signal.determinedFromId == "manualViaScript") return;

      if (signal.type.equals(SignalTypes.Unknown) || true)
        if (isActor) {
          if (channel.channelType != ChannelType.Unknown) {
            const oldSignalType = signal.type;
            const oldDetermineId = signal.determinedFromId;
            determineActorComObjectFunction(signal, channel.channelType);
            if (oldSignalType != signal.type && signal.type.equals(SignalTypes.Unknown)) {
              signal.type = oldSignalType;
              if (oldDetermineId) {
                signal.determinedFromId = oldDetermineId;
              }
            }
          }
        } else {
          let channelType = channel.channelType;

          if (channelType == ChannelType.Unknown || channelType == ChannelType.UnknownMapWithScript) {
            channelType = ChannelType.Unknown;

            if (channel.requestChannel) {
              if (
                channel.requestChannel.channelType == ChannelType.UnknownMapWithScript ||
                channel.requestChannel.channelType == ChannelType.Unknown
              ) {
                //
                // try to get channelType from consumer
                //
                const consumer = channel.consumer;
                if (consumer) {
                  channelType = consumer.channelType;
                }
              } else {
                //
                // use channelType from requestChannel
                //
                channelType = channel.requestChannel.channelType;
              }
            }
          }

          if (channelType != ChannelType.Unknown) {
            if (channel.channelType == ChannelType.Unknown) {
              channel.channelType = channelType;
            }

            if (signal.determinedFromId == "manual" || signal.determinedFromId == "manualViaScript") return;

            const oldSignalType = signal.type;
            const oldDetermineId = signal.determinedFromId;
            determineButtonComObjectFunction(signal, channelType);
            if (oldSignalType != signal.type && signal.type.equals(SignalTypes.Unknown)) {
              signal.type = oldSignalType;
              if (oldDetermineId) {
                signal.determinedFromId = oldDetermineId;
              }
            }
            if (signal.requestSignal) {
              signal.requestSignal.type = signal.type;
            }
          }
        }
    });
  });
}

export function renumberAllphysicalAddresses() {
  // only from tree out
  state.updateAreas(true);

  state.topologies.forEach((area) => {
    area.lines.forEach((line) => {
      renumberPhysicalAddressesForAreaLine(area.areaNumber, line.lineNumber);
    });
  });
}

export function renumberPhysicalAddressesForAreaLine(areaNumber: string | number, lineNumber: string | number) {
  const sLineNumber = lineNumber.toString();
  const area = state.topologies.findFirst((a) => a.areaNumber.toString() == areaNumber.toString());
  const line = area?.lines.findFirst((l) => l.lineNumber.toString() == sLineNumber);
  //
  // get all devices for this line and area
  //

  const sAreaNumber = areaNumber.toString();
  const devices = state
    .allDevices()
    .filter((device) => device.lineAddress === sLineNumber && device.areaAddress === sAreaNumber);

  //
  // filter only devices
  //

  let actors = devices.filter((device) => device.isActor);
  actors = sortArrayByProperty(actors, "fullTag");

  const startAdrButtons = (Math.floor(actors.length / 10) + 2) * 10 + 1;

  let adr = 2;
  actors.forEach((actor) => {
    if (channelTypeInfo(actor.channelType).isSupply) {
      actor.physicalAddress = ``;
    } else {
      //
      // addresen 0 & 1 are reserved for coppler & routers
      // These adresses are set manually from the user or by estada and may not be modified here
      // physical Adresses has to be a number
      //
      if (Number(actor.physicalAddress) >= 2 || actor.physicalAddress == "") {
        actor.physicalAddress = `${adr++}`;
      }
    }
    actor.flagErrors({ doublePhysicalAdresses: "" }, false);
  });

  //
  // filter only buttons
  //

  adr = startAdrButtons;

  let buttons = devices.filter((device) => !device.isActor);
  buttons = sortArrayByProperty(buttons, "fullTag");

  buttons.forEach((button) => {
    button.physicalAddress = `${adr++}`;
    button.flagErrors({ doublePhysicalAdresses: "" }, false);
  });
  if (area && line) {
    area.raiseOnChange("lines");
    line.raiseOnChange("childCollection");
  }
}

export function getDuplicatePhysicalAdressDevices(): Array<Device> {
  const duplicatesFound = new Array<Device>();
  state
    .allDevices()
    .sort((a, b) => a.fullPhysicalAddress.localeCompare(b.fullPhysicalAddress))
    .forEach((device) => {
      if (device.getFullPhysicalAdressIsDouble()) {
        duplicatesFound.push(device);
      }
    });
  return duplicatesFound;
}

export function renumberDuplicatephysicalAddresses(duplicatesFound: Array<Device>) {
  // reduplizieren nur die duplicated numbers eine lassen und alle anderen auf eine freie Adresse setzen
  let devicesToRenumber = sortArrayByProperty(duplicatesFound, "fullTag");
  devicesToRenumber = devicesToRenumber.filter((device) => device.physicalAddress != "-");

  devicesToRenumber.forEach((device) => {
    // Behalte Area und Line bei
    const area = device.areaAddress;
    const line = device.lineAddress;

    // Sammle alle verwendeten physikalischen Adressen in derselben Area/Line
    const usedAddresses = new Set<number>();
    state.allDevices().forEach((dev) => {
      if (dev.areaAddress === area && dev.lineAddress === line && dev !== device) {
        const addr = parseInt(dev.physicalAddress);
        if (!isNaN(addr) && addr >= 0) {
          usedAddresses.add(addr);
        }
      }
    });

    // Bestimme Startadresse basierend darauf, ob es ein Taster ist
    const minAddress = device.isButton ? 22 : 2; // Taster über 21, andere ab 2

    // Finde die nächste freie Adresse
    let freeAddress = minAddress;
    while (usedAddresses.has(freeAddress)) {
      freeAddress++;
    }

    // Setze die neue physikalische Adresse (nur Device-Teil)
    device.physicalAddress = `${freeAddress}`;
  });
}

if (isUiModeDeveloper()) {
  import("../KnxMappings/pretranslateSignalDetectionTexts");
}
